/**
 * Manual slide editor — a Figma-style direct-manipulation layer over a
 * designed slide (slice 1: select / move / resize / inline-text-edit, with
 * brand-grid snapping + alignment guides, undo-redo, and recover/save/cancel).
 *
 * How it works
 * ------------
 * A slide is a self-contained 1920×1080 HTML document stored in
 * `deck_slides.html_content`. We render it in a same-origin sandboxed iframe
 * sized at the native 1920×1080 and CSS-scale the iframe element down to the
 * column width (the same trick the read-only viewer uses) — so *inside* the
 * iframe one CSS pixel equals one canvas unit and pointer coordinates map
 * straight through with no math.
 *
 * `slide-fit.js` (the runtime shrink-to-fit pass) is stripped from the edit
 * document so the canvas stays at its authored geometry while you work; it is
 * restored on save so the viewer + exports keep their fitting behaviour.
 *
 * All interaction (selection box, resize handles, alignment guides, the faint
 * grid raster, measurement badges) is rendered by React in an OVERLAY that
 * sits above the iframe in the parent document — we never inject editor chrome
 * into the slide markup, so what we serialise back is clean. Geometry edits
 * mutate inline styles on the real element node inside the iframe; on first
 * drag/resize an in-flow element is "lifted" to absolute positioning against
 * the `.slide` box (its own offset parent), which is what lets it move freely.
 *
 * Persistence is the `save-manual-edits` deck action: it stores the serialised
 * document into html_content, snapshots a timeline version, and clears the
 * slide's approval (the visible content changed).
 */
import React from 'react';
import { getMargins } from '../lib/slide-render.js';

// Authored canvas + master-grid constants (mirror public/brand/slide-shell.css).
const CANVAS_W = 1920;
const CANVAS_H = 1080;
// Brand safe-area margin (the magenta guardrail where furniture lives) is read
// LIVE from the standard via getMargins() at render (tokens.md → the 40px
// module), so it always tracks the safe area. Everything snaps to the 40px
// modular grid — the one foundation the safe area and content share (1920/48,
// 1080/27). The live margins + centre lines are added per gesture in
// collectSnapTargets. (The legacy 54px / 6-column snap grid was retired.)
const UNIT = 40;
const SNAP = 7;             // snap threshold in canvas px
const GRID_V = Array.from({ length: CANVAS_W / UNIT + 1 }, (_, i) => i * UNIT);
const GRID_H = Array.from({ length: CANVAS_H / UNIT + 1 }, (_, i) => i * UNIT);

// Brand palette (public/brand/colors_and_type.css) — the only colours the
// editor offers, so hand-edits stay on the Eraneos / Eve standard.
const BRAND_COLORS = [
  { name: 'Orange', value: '#FF6428' },
  { name: 'Orange deep', value: '#DE3015' },
  { name: 'Black', value: '#202020' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Warm grey', value: '#F3ECE6' },
  { name: 'Warm grey light', value: '#FCF8F7' },
  { name: 'Taupe', value: '#C9BCB0' },
  { name: 'Warm grey text', value: '#93867D' },
  { name: 'Warm grey deep', value: '#6C5F5B' },
  { name: 'Warm grey darker', value: '#4C3F3B' },
  // Supporting palette — data-viz / accents.
  { name: 'Purple', value: '#715AAE' },
  { name: 'Purple deep', value: '#471D65' },
  { name: 'Purple light', value: '#BFA6E6' },
  { name: 'Red', value: '#E4091F' },
  { name: 'Red deep', value: '#A20235' },
  { name: 'Blue light', value: '#BBD2FF' },
  { name: 'Peach', value: '#FFC3B1' },
  { name: 'Green', value: '#00AF37' },
];

// CI type presets (var names from colors_and_type.css; sizes from slide-shell.css).
const FONT_FAMILIES = [
  { name: 'Display', value: "var(--font-display)" },
  { name: 'Sans (Mona)', value: "var(--font-sans)" },
];
const FONT_WEIGHTS = [
  { name: 'Body 450', value: '450' },
  { name: 'Medium 500', value: '500' },
  { name: 'Semibold 600', value: '600' },
  { name: 'Bold 700', value: '700' },
];
// Named slide-type sizes from the master shell (px), for the size dropdown.
const FONT_SIZES = [24, 28, 40, 48, 60, 80, 100, 120, 152, 184, 240];

// 9-point alignment cells (h, v), row-major, for the on-selection align grid.
const ALIGN9 = [['left', 'top'], ['center', 'top'], ['right', 'top'], ['left', 'middle'], ['center', 'middle'], ['right', 'middle'], ['left', 'bottom'], ['center', 'bottom'], ['right', 'bottom']];
// Normalise a CSS colour (rgb()/#rgb/#rrggbb) to #rrggbb for a native colour input.
function toHex(c) {
  if (!c) return '#202020';
  if (c[0] === '#') return c.length === 7 ? c : (c.length === 4 ? `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}` : '#202020');
  const m = c.match(/\d+/g); if (!m || m.length < 3) return '#202020';
  return `#${m.slice(0, 3).map(n => (+n).toString(16).padStart(2, '0')).join('')}`;
}

// The 15 brand archetypes (functions/_lib/skills.ts) for the "change slide type"
// dropdown. The exact name is what the regenerate refine + designer expect.
const ARCHETYPES = [
  'CoverSlide', 'AgendaSlide', 'ChapterSlide', 'ContentTextSlide', 'ContentReferenceSlide',
  'ImageTextSlide', 'StatsSlide', 'TeamSlide', 'ProjectSlide', 'AnalyticsSlide',
  'SectionDividerSlide', 'QuoteSlide', 'EveIntroSlide', 'SummarySlide', 'ThankYouSlide',
];

// Elements we never let the user select/move — slide chrome that the brand
// standard pins (corner symbol, footer, page number) and the structural wrappers.
const LOCKED = /\b(stage|slide__symbol|slide__footer|slide__wordmark|slide__page|cover__logo)\b/;
const STRUCTURAL = new Set(['HTML', 'BODY']);

function isSelectable(el, slideEl) {
  if (!el || el === slideEl) return false;
  if (STRUCTURAL.has(el.tagName)) return false;
  const cls = typeof el.className === 'string' ? el.className : '';
  if (LOCKED.test(cls)) return false;
  // Don't allow selecting an ancestor that *contains* the whole slide.
  if (el.contains(slideEl)) return false;
  return true;
}

// Inline-level tags that are decorative wrappers, not movable boxes — clicking
// the bold word inside a paragraph should select the PARAGRAPH, not the <b>.
const INLINE_TAGS = new Set(['B', 'I', 'EM', 'STRONG', 'SPAN', 'A', 'U', 'SMALL', 'SUP', 'SUB', 'BR', 'MARK', 'CODE']);
function isInlineish(el) {
  if (INLINE_TAGS.has(el.tagName)) return true;
  try { return /^inline/.test(el.ownerDocument.defaultView.getComputedStyle(el).display); } catch { return false; }
}
// A structural boundary that separates one top-level OBJECT from the next: the
// template zone box, the zfit fitter, or the content layer. We never walk past
// one when hunting for the object a click belongs to.
function isBoundary(el) {
  if (!el || !el.classList) return false;
  return el.classList.contains('zfit') || el.classList.contains('content-v2')
    || (el.hasAttribute && (el.hasAttribute('data-zone') || el.hasAttribute('data-fit')));
}

// Resolve a raw hit-test element to the INNERMOST addressable target — the named
// element (data-el), image, or text leaf actually under the pointer, skipping
// inline runs. This is the deepest thing a click could mean; `resolveSelectable`
// then decides how far OUT to select from here (Figma top-level-first model).
function resolveTarget(hit, slideEl) {
  let el = hit;
  while (el && (!isSelectable(el, slideEl) || isInlineish(el))) el = el.parentElement;
  if (!el) return null;
  const named = el.closest && el.closest('[data-el]');
  if (named && isSelectable(named, slideEl)) return named;
  // A real, addressable element is a data-el, an image, or a text leaf. Anything
  // else hit here is a TRANSPARENT layout wrapper (.zfit / flex row / the
  // content shell) — the layer tree walks straight through these, so the canvas
  // must too: don't let a click on the loose gap select a contentless wrapper
  // <div>. If the wrapper holds exactly one named element, select that; else
  // treat the click as empty (deselect).
  if (el.tagName === 'IMG' || el.tagName === 'SVG' || isTextLeaf(el)) return el;
  const inner = el.querySelectorAll && el.querySelectorAll('[data-el]');
  if (inner && inner.length === 1 && isSelectable(inner[0], slideEl)) return inner[0];
  return null;
}

// The auto-row `group` (deck-v2 wraps a row/one card of peers in a fill-less
// `data-el="group"`) is a LAYOUT wrapper the user sees straight through — it has
// no fill, border or image of its own. Selection must walk through it to the
// visible box inside (just as the layer tree walks through cell wrappers), so a
// click lands on the actual rectangle, not an invisible full-zone container. A
// group that DOES carry a fill/border is a real card and stays selectable.
function isPassthroughWrapper(el, slideEl) {
  const de = el.getAttribute && el.getAttribute('data-el');
  if (de !== 'group') return false;
  try {
    const cs = el.ownerDocument.defaultView.getComputedStyle(el);
    const bg = (cs.backgroundColor || '').replace(/\s/g, '');
    const hasBg = bg && bg !== 'transparent' && !/rgba\(0,0,0,0\)/.test(bg);
    const hasBorder = ['Top', 'Right', 'Bottom', 'Left'].some(s => parseFloat(cs['border' + s + 'Width']) > 0);
    const hasImg = cs.backgroundImage && cs.backgroundImage !== 'none';
    return !hasBg && !hasBorder && !hasImg;
  } catch { return true; }
}

// The TOP-LEVEL object a target belongs to: the OUTERMOST *visible* named box
// between the target and the zone/zfit boundary. A "rectangle with text" (a card)
// carries its background fill on this box while its title/body are nested
// `data-el` children — so selecting it is what lets the whole rectangle (fill +
// text) move and resize as one unit, instead of dragging the text off its fixed
// background ("text on front, background stuck"). Transparent auto-`group`
// wrappers are skipped so selection lands on the card the user actually sees.
function topObjectOf(target, slideEl) {
  let top = target, p = target.parentElement;
  while (p && p !== slideEl && !isBoundary(p)) {
    if (p.hasAttribute && p.hasAttribute('data-el') && isSelectable(p, slideEl) && !isPassthroughWrapper(p, slideEl)) top = p;
    p = p.parentElement;
  }
  return top;
}

// Figma-style hit resolution. A plain click selects the TOP-LEVEL object under
// the pointer (the whole card/rectangle incl. its fill). Clicking again on an
// object that is already selected DRILLS IN one level toward what was actually
// under the cursor, so inner elements stay reachable on the canvas (the Layers
// panel and "↑ Parent" cover the rest). `current` is the live selection.
function resolveSelectable(hit, slideEl, current) {
  const target = resolveTarget(hit, slideEl);
  if (!target) return null;
  const top = topObjectOf(target, slideEl);
  if (top === target) return top;                    // already a leaf object
  // Drill in only when the user clicks WITHIN the object they already have
  // selected; a click on a different object jumps straight to that object's top.
  const inCurrent = current && (current === top || top.contains(current));
  if (inCurrent && current !== target) {
    let c = target;
    while (c && c.parentElement && c.parentElement !== current) c = c.parentElement;
    if (c && c !== current && isSelectable(c, slideEl)) return c;
  }
  return inCurrent ? target : top;
}

// ---- layer tree (derived live from the rendered DOM) ----------------------
// Humanize an element id for the tree (kpiTile → "KPI tile", bigStatement → "Big
// statement"). data-el is stamped by deck-v2's elHTML.
function humanizeEl(id) {
  const s = String(id || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
  return s.replace(/\bkpi\b/gi, 'KPI').replace(/^./, c => c.toUpperCase());
}
function nodeSnippet(el) {
  const t = (el.textContent || '').replace(/\s+/g, ' ').trim();
  return t.length > 38 ? `${t.slice(0, 36)}…` : t;
}
// A text-bearing leaf: a block with text and no nested named element / media and
// no block child that itself carries text (so a KPI number/label is a leaf).
function isTextLeaf(el) {
  if (el.hasAttribute('data-el')) return false;
  if (el.querySelector('[data-el], img, svg')) return false;
  if (!(el.textContent || '').trim()) return false;
  for (const ch of el.children) {
    if (ch.nodeType !== 1) continue;
    if (!INLINE_TAGS.has(ch.tagName) && (ch.textContent || '').trim()) return false;
  }
  return true;
}
function layerLabel(el) {
  if (el.hasAttribute('data-el')) return humanizeEl(el.getAttribute('data-el'));
  if (el.tagName === 'IMG' || el.tagName === 'SVG') return 'Image';
  if (/^H[1-6]$/.test(el.tagName)) return 'Heading';
  if (el.tagName === 'UL' || el.tagName === 'OL') return 'List';
  if (el.tagName === 'LI') return 'List item';
  return 'Text';
}
// Build the nested node tree from the slide DOM. Roots are the template zones
// (data-zone). Transparent layout wrappers (zfit / flex rows) are walked through
// so the tree shows meaningful boxes (data-el) and their inner text leaves.
function buildLayerNodes(slideEl) {
  if (!slideEl) return [];
  const content = slideEl.querySelector('.content-v2') || slideEl;
  const childNodes = (el) => {
    const out = [];
    for (const ch of el.children) {
      if (ch.nodeType !== 1) continue;
      const cls = typeof ch.className === 'string' ? ch.className : '';
      if (LOCKED.test(cls)) continue;
      const named = ch.hasAttribute('data-el');
      const leaf = !named && (isTextLeaf(ch) || ch.tagName === 'IMG' || ch.tagName === 'SVG');
      if (named || leaf) {
        const kids = leaf ? [] : childNodes(ch);
        // Element-as-itself: a named element whose only children are its own text
        // leaves shows as ONE row (the row already previews its text) — no nested
        // "Text". Keep children only when they are real nested elements / groups.
        const meaningful = named ? kids.filter(k => k.children.length || !/^(Text|Heading|List|List item)$/.test(k.label)) : kids;
        out.push({ el: ch, label: layerLabel(ch), text: nodeSnippet(ch), children: meaningful });
      } else out.push(...childNodes(ch));   // walk through a transparent wrapper
    }
    // Order by VISUAL position (top, then left) so the tree mirrors what you see:
    // drag an element below another on the canvas and it drops below it here too.
    return out.sort((a, b) => {
      const ra = a.el.getBoundingClientRect(), rb = b.el.getBoundingClientRect();
      return (Math.round(ra.top) - Math.round(rb.top)) || (ra.left - rb.left);
    });
  };
  const zones = [...content.querySelectorAll('[data-zone]')];
  if (zones.length) {
    return zones.map(z => ({ el: z, label: (z.getAttribute('data-zone') || 'zone').toUpperCase(), text: '', isZone: true, children: childNodes(z) }))
      .filter(z => z.children.length);
  }
  return childNodes(content);
}

// Rect of `el` in canvas coordinates (relative to the slide's top-left).
function canvasRect(el, slideEl) {
  const er = el.getBoundingClientRect();
  const sr = slideEl.getBoundingClientRect();
  return { x: er.left - sr.left, y: er.top - sr.top, w: er.width, h: er.height };
}

// Pin one element to absolute at slide-space `want`, resolving left/top against
// whatever becomes its offset parent (an inner card, not necessarily the slide):
// zero left/top, measure where that lands (the "base"), then offset by it. So
// positioning is offset-parent-agnostic and an element never jumps to the wrong
// box. When `fixHeight` we also pin the height (used when freezing siblings so
// the container holds its shape); the actively-moved element keeps auto height.
function pinAbsolute(el, slideEl, want, fixHeight) {
  const s = el.style;
  s.boxSizing = 'border-box';
  s.margin = '0';
  s.transform = 'none';
  // Clear every CSS constraint that could fight manual sizing/positioning:
  //  - min/max box caps (slide text blocks carry e.g. max-width:1640px) would
  //    silently clamp an enlarged box;
  //  - right/bottom/inset would keep re-anchoring the element so setting
  //    left/top alone wouldn't move it;
  //  - aspect-ratio would couple width and height.
  s.maxWidth = 'none';
  s.maxHeight = 'none';
  s.minWidth = '0';
  s.minHeight = '0';
  s.right = 'auto';
  s.bottom = 'auto';
  s.inset = 'auto';
  s.aspectRatio = 'auto';
  s.width = `${Math.round(want.w)}px`;
  if (fixHeight) s.height = `${Math.round(want.h)}px`;
  s.position = 'absolute';
  s.left = '0px';
  s.top = '0px';
  const base = canvasRect(el, slideEl);
  el.dataset.edBaseX = String(base.x);
  el.dataset.edBaseY = String(base.y);
  // Round the canvas coordinate, not the offset — the base is often fractional
  // and rounding the offset would land the element off the pixel grid (see
  // setCanvasXY).
  s.left = `${Math.round(want.x) - base.x}px`;
  s.top = `${Math.round(want.y) - base.y}px`;
  if (!s.zIndex) s.zIndex = '5';
  el.dataset.edLifted = '1';
}

// Freeze a container's layout BEFORE we lift one of its children: pin EVERY
// in-flow child to absolute at its current position, so pulling one child out
// of flow can't reflow (jump) its siblings — the #1 cause of "elements jump
// when I move one". Capture all geometry first, then mutate. Idempotent per
// container. Already-positioned chrome (footer/symbol/scene) is left untouched.
function freezeContainer(parent, slideEl) {
  if (!parent || parent.dataset.edFrozen || parent === slideEl?.ownerDocument?.body) return;
  const view = parent.ownerDocument.defaultView;
  const kids = [...parent.children].filter((c) => {
    if (c.nodeType !== 1) return false;
    const pos = view.getComputedStyle(c).position;
    return pos === 'static' || pos === 'relative';   // only flow children reflow
  });
  if (kids.length === 0) { parent.dataset.edFrozen = '1'; return; }
  const kidRects = kids.map(c => canvasRect(c, slideEl));
  // Give the parent a positioning context + hold its slot in the OUTER flow so
  // its own siblings don't reflow either. The slide itself is already sized.
  if (parent !== slideEl) {
    const pr = canvasRect(parent, slideEl);
    const pcs = view.getComputedStyle(parent);
    if (pcs.position === 'static') parent.style.position = 'relative';
    parent.style.boxSizing = 'border-box';
    parent.style.width = `${Math.round(pr.w)}px`;
    parent.style.height = `${Math.round(pr.h)}px`;
  }
  parent.dataset.edFrozen = '1';
  kids.forEach((c, i) => pinAbsolute(c, slideEl, kidRects[i], true));
}

// True if `el` has at least one BLOCK-level element child — i.e. it's a layout
// container (vs. a text block whose children are inline runs like <b>/<span>,
// which we must NOT flatten or the text would break apart).
function hasBlockChildren(el, view) {
  for (const ch of el.children) {
    if (ch.nodeType !== 1) continue;
    const d = view.getComputedStyle(ch).display;
    if (d && !/^inline|^none|^contents/.test(d)) return true;
  }
  return false;
}

// Flatten the slide to absolute positioning when editing starts, so every
// element becomes independently movable. We recurse through structural
// containers (slide → content-v2 → zones → zfit wrappers) but STOP at the
// .zfit level: the individual elements inside .zfit get pinned to absolute,
// but their INTERNAL structure (text divs inside Hero, Subhead, etc.) stays
// in normal flow. Flattening deeper would break parent-child containment.
function flattenSlide(slideEl) {
  const view = slideEl.ownerDocument.defaultView;
  // A previously saved hand-edit may carry the v2 fit pass's baked
  // transform:scale() on the .zfit wrappers as plain inline styles (the script
  // that owned them is stripped from the edit doc, so nothing would ever reset
  // them). Clear them BEFORE measuring/pinning anything, or every rect under a
  // scaled wrapper reads (and writes back) distorted geometry.
  slideEl.querySelectorAll('.zfit').forEach((f) => { f.style.transform = ''; f.style.transformOrigin = ''; });
  const walk = (container) => {
    const layoutKids = [...container.children].filter(
      c => c.nodeType === 1 && hasBlockChildren(c, view) && !LOCKED.test(typeof c.className === 'string' ? c.className : ''),
    );
    freezeContainer(container, slideEl);
    if (container.classList?.contains('zfit')) return;
    layoutKids.forEach(walk);
  };
  walk(slideEl);
}

// Promote an element to absolute so it can move/resize freely — freezing its
// container first so siblings stay put, then ensuring the element itself is
// pinned (it usually already is, by the freeze).
function liftToAbsolute(el, slideEl) {
  const want = canvasRect(el, slideEl);
  freezeContainer(el.parentElement, slideEl);
  if (!el.dataset.edLifted) pinAbsolute(el, slideEl, want, false);
  return canvasRect(el, slideEl);
}

// Move a lifted element so its top-left lands at slide-space (x, y), accounting
// for the element's offset-parent base captured at lift time. Round the CANVAS
// coordinate, not the CSS offset: the base is often fractional (flow layouts
// produce half-pixel positions), and rounding the offset would leave the
// element off the integer/40px grid by that residue — type X=40, land at 40.5,
// read back 41.
function setCanvasXY(el, x, y) {
  const bx = parseFloat(el.dataset.edBaseX || '0');
  const by = parseFloat(el.dataset.edBaseY || '0');
  el.style.left = `${Math.round(x) - bx}px`;
  el.style.top = `${Math.round(y) - by}px`;
}

// A numeric field that holds a local draft while you type and commits ONLY on
// Enter or blur. Committing per keystroke would apply every intermediate value
// (typing "120" → 1, then 12, then 120) — each one mutating the live element
// and making the OTHER geometry fields jump while you type. Esc discards the
// draft. `min` rejects out-of-range values (e.g. W/H pass min=1 so an empty box
// or 0 is never saved); X/Y/Pad/Gap omit it so 0 commits.
export function NumInput({ value, onCommit, min, step, style, title }) {
  const [draft, setDraft] = React.useState(null);
  const shown = draft != null ? draft : (value == null || Number.isNaN(value) ? '' : value);
  const commit = (raw) => {
    if (raw === '') return;                         // cleared — keep the old value
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return;
    if (min != null && n < min) return;             // reject 0 / negatives for W/H
    onCommit(n);
  };
  return (
    <input
      type="number" step={step} value={shown} style={style} title={title}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { commit(e.currentTarget.value); setDraft(null); e.currentTarget.blur(); }
        else if (e.key === 'Escape') { setDraft(null); e.currentTarget.blur(); }
      }}
      onBlur={(e) => { if (draft != null) commit(e.target.value); setDraft(null); }}
    />
  );
}

export function SlideEditor({ html, slideIndex, busy, onSave, onCancel, loadVersionHtml, onChangeType, currentLayout, master, railTop, railBottom, belowCanvas, onAddTo, fill, slideTitle, onAutoSave, editorApiRef, saveLabel, cancelLabel }) {
  const wrapRef = React.useRef(null);
  const zoomScrollRef = React.useRef(null);
  const iframeRef = React.useRef(null);
  const [scale, setScale] = React.useState(0);
  const [zoom, setZoom] = React.useState(1);              // scroll-to-zoom multiplier (1 = fit)
  const [showGuides, setShowGuides] = React.useState(true);  // magenta safe-area margins
  const [showZones, setShowZones] = React.useState(true);    // cyan Level-2 containers
  const [show40, setShow40] = React.useState(true);       // 40px modular grid (magenta) — the ONE foundation grid, on by default
  const [zoneRects, setZoneRects] = React.useState([]);   // underlying L2 zones (canvas coords)
  const collapsedRef = React.useRef(new Set());           // layer-tree nodes collapsed by the user
  const [treeTick, setTreeTick] = React.useState(0);      // bump to re-render the layer tree
  const toggleCollapse = React.useCallback((el) => { const s = collapsedRef.current; if (s.has(el)) s.delete(el); else s.add(el); setTreeTick(t => t + 1); }, []);

  // The working document shown in the iframe. We strip slide-fit so the canvas
  // doesn't get shrunk under us, and inject a tiny style for the text caret.
  const prepareEditDoc = React.useCallback((src) => {
    let s = String(src || '');
    // Drop the runtime fit script (any path) so it can't transform the canvas.
    s = s.replace(/<script[^>]*slide-fit\.js[^>]*>\s*<\/script>/gi, '');
    // Same for the inline v2 fit pass (<script data-v2fit>): it re-applies
    // transform:scale() to the .zfit wrappers on load / fonts-ready / +300ms,
    // which silently scales EVERY measurement under the editor — type W=1822,
    // measure back 1796 through a 0.985 scale — and re-fits against manual
    // resizes so they appear not to apply. Editing happens at authored
    // geometry; the saved slide carries data-edited so the fit stays off.
    s = s.replace(/<script[^>]*data-v2fit[^>]*>[\s\S]*?<\/script>/gi, '');
    // Editing: never clip element content (the v2 boxes use overflow:hidden as a
    // render-time safety with the fit script — which we strip here — so without it
    // they'd cut text off). Show everything while the user edits/resizes.
    const edStyle = `<style id="__ed_style">@keyframes __edFieldPulse{0%,100%{outline-color:#FF6428}50%{outline-color:rgba(255,100,40,0.25)}}[contenteditable="true"]{outline:2px solid #FF6428;outline-offset:2px;animation:__edFieldPulse 1.1s ease-in-out infinite;caret-color:#FF6428;}html,body{cursor:default;}.content-v2 *:not([data-ed-clip]){overflow:visible !important;}[data-ed-clip]{overflow:hidden !important;}</style>`;
    if (/<\/head>/i.test(s)) s = s.replace(/<\/head>/i, `${edStyle}</head>`);
    else s = edStyle + s;
    return s;
  }, []);

  // Undo/redo history of serialised edit-docs (cap 100). historyRef holds the
  // stack; `version` bumps to force the iframe to reload from history[idx].
  const historyRef = React.useRef([]);
  const idxRef = React.useRef(-1);
  const [hist, setHist] = React.useState({ idx: -1, len: 0 });
  // The doc currently fed to the iframe. Changing it reloads the frame.
  const [doc, setDoc] = React.useState(() => prepareEditDoc(html));
  // The doc value the iframe has finished loading — gates the iframe's opacity
  // so a reload never flashes the previous (or pre-flatten) frame.
  const [loadedDoc, setLoadedDoc] = React.useState(null);
  // When true, the next onLoad is a programmatic reload (undo/redo/recover) and
  // must NOT push a new history entry.
  const reloadingRef = React.useRef(false);

  // Selection + live-interaction state.
  const selRef = React.useRef(null);                 // the selected DOM node (in iframe)
  const [sel, setSel] = React.useState(null);        // { x,y,w,h, tag, label } in canvas coords
  const [guides, setGuides] = React.useState({ v: [], h: [] });
  const [badges, setBadges] = React.useState([]);    // measurement badges during a drag
  const [editingText, setEditingText] = React.useState(false);
  const [showGrid, setShowGrid] = React.useState(false);   // old 6-column brand grid — OFF; the 40px grid is the one we show
  const [dirty, setDirty] = React.useState(false);
  const [recovering, setRecovering] = React.useState(false);
  const dragRef = React.useRef(null);                // active drag/resize gesture
  const lastTapRef = React.useRef(null);             // { el, t, x, y } for double-tap → text edit
  const keyHandlerRef = React.useRef(null);          // latest keydown handler, shared with the iframe doc
  // Phase-2 state: clipboard (copy/paste), right-click menu, the selected
  // element's current text styles (to seed the type/colour panels), the active
  // colour target (text vs fill), and the chosen archetype for "change type".
  const clipboardRef = React.useRef(null);           // outerHTML of a copied element
  const [menu, setMenu] = React.useState(null);      // { x, y } overlay coords for the context menu
  const [selStyle, setSelStyle] = React.useState(null); // { color, fontFamilyKind, fontWeight, fontSize }
  const [colorTarget, setColorTarget] = React.useState('text');
  const [colorPop, setColorPop] = React.useState(null);   // edit-table colour popover: null | 'text' | 'fill'
  const [imgBusy, setImgBusy] = React.useState(false);    // change-image request in flight
  const [imgErr, setImgErr] = React.useState('');
  const [imgQ, setImgQ] = React.useState('');             // Unsplash search query
  const [imgPrompt, setImgPrompt] = React.useState('');   // AI generation prompt
  const [typeChoice, setTypeChoice] = React.useState(currentLayout || '');

  // Fit the 1920×1080 canvas inside the scroll viewport. At zoom 1 the whole
  // slide is visible (no scroll); scroll-to-zoom scales past fit and the
  // viewport scrolls. Scale follows BOTH width and height so the slide is never
  // taller than the available space in the default (fit) state.
  // CRISP RULE: the scale is quantised to 1/40 steps so one 40px module maps to
  // a whole number of screen pixels — the modular grid, the zone hairlines and
  // the slide content all rasterise on the same pixel boundaries (no half-pixel
  // shimmer, and overlay clicks line up 1:1 with what the eye sees).
  React.useLayoutEffect(() => {
    const el = zoomScrollRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) {
        const raw = Math.min(w / CANVAS_W, h / CANVAS_H) * zoom;
        setScale(Math.max(0.1, Math.floor(raw * 40) / 40));
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoom]);

  // Scroll to zoom (in steps), keeping the 40px grid crisp; non-passive so we can
  // stop the page from scrolling under the cursor.
  React.useEffect(() => {
    const el = zoomScrollRef.current; if (!el) return undefined;
    const onWheel = (e) => { e.preventDefault(); const d = e.deltaY < 0 ? 0.1 : -0.1; setZoom(z => Math.max(0.4, Math.min(3, +(z + d).toFixed(2)))); };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const idoc = () => iframeRef.current && iframeRef.current.contentDocument;
  const slideEl = () => { const d = idoc(); return d && d.querySelector('.slide'); };

  // Serialise the live iframe document back to a clean, slide-fit-restored
  // slide HTML string (used for save). Strips editor-only bits.
  const serialize = React.useCallback((restoreFit) => {
    const d = idoc();
    if (!d) return null;
    const clone = d.documentElement.cloneNode(true);
    const st = clone.querySelector('#__ed_style');
    if (st) st.remove();
    clone.querySelectorAll('[contenteditable]').forEach(n => n.removeAttribute('contenteditable'));
    clone.querySelectorAll('[data-ed-lifted]').forEach(n => { n.removeAttribute('data-ed-lifted'); n.removeAttribute('data-ed-base-x'); n.removeAttribute('data-ed-base-y'); });
    clone.querySelectorAll('[data-ed-frozen]').forEach(n => n.removeAttribute('data-ed-frozen'));
    clone.querySelectorAll('[data-ed-clip]').forEach(n => { n.removeAttribute('data-ed-clip'); n.style.overflow = 'hidden'; });
    clone.querySelectorAll('[data-ed-grow]').forEach(n => n.removeAttribute('data-ed-grow'));
    clone.querySelectorAll('[data-fit-inner]').forEach(n => n.removeAttribute('data-fit-inner'));
    // Mark the slide as hand-edited so slide-fit keeps the authored geometry
    // (skips its shrink-to-fit pass) — what you placed is what renders/exports.
    const sl = clone.querySelector('.slide');
    if (sl) sl.setAttribute('data-edited', '1');
    if (restoreFit && !/slide-fit\.js/i.test(clone.innerHTML)) {
      const body = clone.querySelector('body');
      if (body) {
        const sc = d.createElement('script');
        sc.src = '/brand/slide-fit.js';
        body.appendChild(sc);
      }
    }
    return `<!doctype html>\n${clone.outerHTML}`;
  }, []);

  // Push the current edit-doc onto the history stack (called after every
  // committed change). Coalesce key holds rapid same-target nudges to one entry.
  const pushHistory = React.useCallback((coalesceKey) => {
    const d = idoc();
    if (!d) return;
    const snap = `<!doctype html>\n${d.documentElement.outerHTML}`;
    const stack = historyRef.current;
    // Drop any redo tail.
    stack.length = idxRef.current + 1;
    const top = stack[idxRef.current];
    if (coalesceKey && top && top.key === coalesceKey) {
      stack[idxRef.current] = { html: snap, key: coalesceKey };
    } else {
      stack.push({ html: snap, key: coalesceKey || null });
      if (stack.length > 100) stack.shift();
      idxRef.current = stack.length - 1;
    }
    setHist({ idx: idxRef.current, len: stack.length });
    setDirty(true);
  }, []);

  // AUTOSAVE — every committed edit (= one history push) persists the canvas
  // back to the host after a short debounce, so navigating away (ground switch,
  // closing the popup, slide nav) never loses work. The explicit Save button
  // remains the APPROVAL action; autosave only protects the edit.
  const autoRef = React.useRef(null);
  React.useEffect(() => {
    if (!onAutoSave || !dirty) return undefined;
    clearTimeout(autoRef.current);
    autoRef.current = setTimeout(() => { const out = serialize(true); if (out) onAutoSave(out); }, 700);
    return () => clearTimeout(autoRef.current);
  }, [hist, dirty, onAutoSave, serialize]);

  // Live-snapshot API for the host: a ground switch must read the CURRENT
  // canvas (not the last saved html) before this editor remounts.
  React.useEffect(() => {
    if (!editorApiRef) return undefined;
    editorApiRef.current = { snapshot: () => serialize(true), isDirty: () => dirty };
    return () => { editorApiRef.current = null; };
  }, [editorApiRef, serialize, dirty]);

  // Clear selection chrome.
  const clearSelection = React.useCallback(() => {
    selRef.current = null;
    setSel(null);
    setGuides({ v: [], h: [] });
    setBadges([]);
    setSelStyle(null);
    setMenu(null);
  }, []);

  const refreshSelRect = React.useCallback(() => {
    const el = selRef.current; const s = slideEl();
    if (!el || !s) return;
    const r = canvasRect(el, s);
    const tag = (el.tagName || '').toLowerCase();
    const cls = (typeof el.className === 'string' ? el.className.split(/\s+/)[0] : '') || '';
    const named = el.getAttribute && el.getAttribute('data-el');
    let parent = el.parentElement;
    while (parent && parent !== s && !parent.dataset.edLifted) parent = parent.parentElement;
    const isNested = parent && parent !== s && parent !== el && !parent.classList?.contains('zfit');
    const pr = isNested ? canvasRect(parent, s) : null;
    setSel({ ...r, tag, label: named ? humanizeEl(named) : (cls ? `${tag}.${cls}` : tag), relX: pr ? r.x - pr.x : r.x, relY: pr ? r.y - pr.y : r.y, parentOfsX: pr ? pr.x : 0, parentOfsY: pr ? pr.y : 0, hasParent: !!isNested });
    // Seed the type / colour / alignment panels from the element's computed style.
    try {
      const cs = el.ownerDocument.defaultView.getComputedStyle(el);
      const ff = (cs.fontFamily || '').toLowerCase();
      const fsPx = parseFloat(cs.fontSize) || 16;
      const lhPx = cs.lineHeight === 'normal' ? fsPx * 1.2 : (parseFloat(cs.lineHeight) || fsPx * 1.2);
      const lsPx = cs.letterSpacing === 'normal' ? 0 : (parseFloat(cs.letterSpacing) || 0);
      const disp = cs.display || '';
      const jc = cs.justifyContent || 'flex-start';
      const ta = cs.textAlign || 'left';
      const isFlex = disp === 'flex' || disp === 'inline-flex';
      const ai = cs.alignItems || '';
      const alignV = isFlex ? (jc.includes('end') ? 'bottom' : jc.includes('center') ? 'middle' : 'top') : 'top';
      const alignH = isFlex ? (ai.includes('center') ? 'center' : ai.includes('end') ? 'right' : 'left') : (ta.includes('center') ? 'center' : ta.includes('right') || ta.includes('end') ? 'right' : 'left');
      const hasFitInner = !!el.querySelector(':scope > [data-fit-inner]');
      const sizeMode = el.getAttribute('data-ed-fit') === 'fill' ? 'fill' : hasFitInner ? 'fit' : el.hasAttribute('data-ed-clip') ? 'clip' : (el.hasAttribute('data-ed-grow') || (parseFloat(cs.flexGrow) || 0) > 0 ? 'grow' : 'auto');
      const padding = parseInt(cs.paddingTop, 10) || 0;
      const padT = parseInt(cs.paddingTop, 10) || 0, padR = parseInt(cs.paddingRight, 10) || 0, padB = parseInt(cs.paddingBottom, 10) || 0, padL = parseInt(cs.paddingLeft, 10) || 0;
      // Container = an explicit nester (group/frame/mask) OR any element that
      // nests named sub-elements (e.g. a composite card holding quote-mark /
      // quote / attribution) → show the auto-layout (Lay/Gap) controls.
      const isContainer = /^(group|frame|mask)$/.test(el.getAttribute('data-el') || '') || !!el.querySelector('[data-el]');
      const layoutMode = !cs.display.includes('flex') ? 'free' : cs.flexWrap === 'wrap' ? 'wrap' : cs.flexDirection === 'row' ? 'row' : 'col';
      const gap = parseInt(cs.gap, 10) || 0;
      setSelStyle({
        padT, padR, padB, padL, isContainer, layoutMode, gap,
        color: cs.color,
        fontFamilyKind: ff.includes('eraneos display') ? 'var(--font-display)' : 'var(--font-sans)',
        fontWeight: String(Math.round(parseInt(cs.fontWeight, 10) / 50) * 50) || cs.fontWeight,
        fontSize: Math.round(parseFloat(cs.fontSize)) || '',
        lineHeightPct: Math.round((lhPx / fsPx) * 100),
        letterEm: +(lsPx / fsPx).toFixed(3),
        alignH, alignV, sizeMode, padding,
        isImage: el.tagName === 'IMG' || /\burl\(/.test(cs.backgroundImage || '') || /image|photo|media|frame|illustration|avatar/i.test(el.getAttribute('data-el') || ''),
      });
    } catch { setSelStyle(null); }
  }, []);

  const selectElement = React.useCallback((el) => {
    selRef.current = el;
    refreshSelRect();
  }, [refreshSelRect]);

  // Apply an edited X/Y/W/H (canvas px) from the on-selection coordinate table:
  // lift to absolute, move/resize, refresh the selection box + push history.
  const applyGeom = (field, raw) => {
    const el = selRef.current; const s = slideEl(); if (!el || !s) return;
    const val = Math.round(Number(raw)); if (!Number.isFinite(val)) return;
    liftToAbsolute(el, s);
    const r = canvasRect(el, s);
    let { x, y, w, h } = r;
    if (field === 'x') x = val;
    else if (field === 'y') y = val;
    else if (field === 'w') { w = Math.max(8, val); el.style.width = `${w}px`; }
    else if (field === 'h') { h = Math.max(8, val); el.style.height = `${h}px`; }
    setCanvasXY(el, x, y);
    if (el.querySelector(':scope > [data-fit-inner]')) applyFitScale(el);
    refreshSelRect(); pushHistory(`geom-${field}`);
  };

  // Per-side inner padding (T/R/B/L) from the edit table.
  const setPad = (side, raw) => {
    const el = selRef.current; if (!el) return;
    const v = Math.max(0, Math.round(Number(raw)) || 0);
    el.style[`padding${side[0].toUpperCase()}${side.slice(1)}`] = `${v}px`;
    refreshSelRect(); pushHistory(`pad-${side}`);
  };
  // Auto-layout of a container's children (Figma-style): free / row / row-wrap /
  // column, with a gap between objects when not free.
  // Release a container's direct children from the absolute pinning that edit
  // mode applies, so they become flex items again and an auto-layout can flow
  // them. (Edit mode freezes children to absolute for free dragging; that
  // pinning is exactly what stops Row/Col/Wrap from repositioning them.)
  const unpinChildren = (el) => {
    [...el.children].forEach(ch => {
      if (ch.nodeType !== 1 || !(ch.dataset && ch.dataset.edLifted)) return;
      ['position', 'left', 'top', 'right', 'bottom', 'inset', 'width', 'height', 'zIndex', 'margin', 'transform', 'maxWidth', 'maxHeight', 'minWidth', 'minHeight', 'flex'].forEach(p => { ch.style[p] = ''; });
      delete ch.dataset.edLifted; delete ch.dataset.edBaseX; delete ch.dataset.edBaseY;
    });
    if (el.dataset) delete el.dataset.edFrozen;
  };
  const setLayout = (mode) => {
    const el = selRef.current, s = slideEl(); if (!el || !s) return;
    if (mode === 'free') {
      el.style.display = ''; el.style.flexWrap = ''; el.style.flexDirection = ''; el.style.alignContent = '';
      // Pin the children where they currently sit so each can be dragged freely.
      freezeContainer(el, s);
    } else {
      // Flow the children: drop their absolute pinning, then let flex arrange them.
      unpinChildren(el);
      el.style.display = 'flex';
      el.style.flexDirection = mode === 'col' ? 'column' : 'row';
      el.style.flexWrap = mode === 'wrap' ? 'wrap' : 'nowrap';
      el.style.alignContent = 'flex-start';
      el.style.alignItems = el.style.alignItems || 'flex-start';
      if (!parseInt(el.style.gap, 10)) el.style.gap = '40px';
    }
    refreshSelRect(); pushHistory('layout');
  };
  const setGap = (raw) => {
    const el = selRef.current; if (!el) return;
    el.style.gap = `${Math.max(0, Math.round(Number(raw)) || 0)}px`;
    refreshSelRect(); pushHistory('gap');
  };
  // Direct line-height (%) and letter-spacing (em) — editable fields, no steppers.
  const setLine = (pct) => { const el = selRef.current; if (!el) return; el.style.lineHeight = String(Math.max(0.5, (Number(pct) || 100) / 100)); refreshSelRect(); pushHistory('line'); };
  const setChar = (em) => { const el = selRef.current; if (!el) return; el.style.letterSpacing = `${Number(em) || 0}em`; refreshSelRect(); pushHistory('char'); };

  // Set a new image source on the selected element: an <img> gets its src; any
  // other element gets a cover background-image. Records one history entry.
  const applyImage = (src) => {
    const el = selRef.current, s = slideEl(); if (!el || !s || !src) return;
    const win = el.ownerDocument.defaultView;
    const img = el.tagName === 'IMG' ? el : el.querySelector('img');
    if (img) { img.src = src; img.removeAttribute('srcset'); }
    else {
      // The cover image may sit on the element itself or on an inner div (deck-v2
      // renders <div data-el="image"><div style="background:url(…)">). Replace it
      // wherever it actually lives.
      let target = el;
      if (!/\burl\(/.test(win.getComputedStyle(el).backgroundImage || '')) {
        target = [...el.querySelectorAll('*')].find(d => /\burl\(/.test(win.getComputedStyle(d).backgroundImage || '')) || el;
      }
      target.style.backgroundImage = `url('${src}')`;
      target.style.backgroundSize = target.style.backgroundSize || 'cover';
      target.style.backgroundPosition = target.style.backgroundPosition || 'center';
      target.style.backgroundRepeat = 'no-repeat';
    }
    refreshSelRect(); pushHistory('image');
  };
  // Fetch a replacement image (Unsplash search / AI generation / upload) and apply it.
  const changeImage = async (payload, isUpload) => {
    if (imgBusy) return; setImgBusy(true); setImgErr('');
    try {
      const res = await fetch('/api/slide-system/image', isUpload
        ? { method: 'POST', credentials: 'same-origin', body: payload }
        : { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...payload, variant: /eve/i.test(master || '') ? 'eve' : 'eraneos' }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.src) throw new Error(j.error || `request failed (${res.status})`);
      applyImage(j.src);
    } catch (e) { setImgErr(String(e?.message || e)); }
    setImgBusy(false);
  };

  // Gather sibling snap lines (every other selectable element's edges/centres)
  // once at gesture start, for alignment snapping + measurement badges.
  const collectSnapTargets = React.useCallback((moving) => {
    const s = slideEl(); if (!s) return { v: [], h: [], rects: [] };
    const mg = getMargins();
    // 40px grid + the live safe-area margins + the canvas centre lines.
    const v = new Set([...GRID_V, mg.left, CANVAS_W - mg.right, CANVAS_W / 2]);
    const h = new Set([...GRID_H, mg.top, CANVAS_H - mg.bottom, CANVAS_H / 2]);
    const rects = [];
    s.querySelectorAll('*').forEach((el) => {
      if (el === moving || moving.contains(el) || el.contains(moving)) return;
      if (!isSelectable(el, s)) return;
      const r = canvasRect(el, s);
      if (r.w < 4 || r.h < 4) return;
      rects.push(r);
      v.add(r.x); v.add(r.x + r.w / 2); v.add(r.x + r.w);
      h.add(r.y); h.add(r.y + r.h / 2); h.add(r.y + r.h);
    });
    return { v: [...v], h: [...h], rects };
  }, []);

  // Snap a set of candidate values to the nearest target line within SNAP.
  // Returns { delta, line } for the best candidate, or null. A 40px-grid line
  // beats a sibling edge whenever one is in range: the modular grid is the
  // foundation, and sibling edges (often off-grid in the authored flow layout)
  // must not pull a drop off it just by being a pixel or two nearer.
  function bestSnap(values, targets) {
    let best = null, grid = null;
    for (const val of values) {
      for (const t of targets) {
        const d = t - val;
        if (Math.abs(d) > SNAP) continue;
        if (!best || Math.abs(d) < Math.abs(best.delta)) best = { delta: d, line: t };
        if (t % UNIT === 0 && (!grid || Math.abs(d) < Math.abs(grid.delta))) grid = { delta: d, line: t };
      }
    }
    return grid || best;
  }

  // ---- pointer interaction on the overlay ----------------------------------
  const overlayPoint = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / scale, y: (e.clientY - r.top) / scale };
  };

  // Anchor points of the 8 resize handles for the current selection.
  const handlePts = () => sel && {
    nw: [sel.x, sel.y], ne: [sel.x + sel.w, sel.y], sw: [sel.x, sel.y + sel.h], se: [sel.x + sel.w, sel.y + sel.h],
    n: [sel.x + sel.w / 2, sel.y], s: [sel.x + sel.w / 2, sel.y + sel.h], w: [sel.x, sel.y + sel.h / 2], e: [sel.x + sel.w, sel.y + sel.h / 2],
  };

  // Hit-test which resize handle (if any) a point is on, in canvas coords.
  const handleAt = (pt) => {
    const pts = handlePts();
    if (!pts) return null;
    const hs = 12 / scale; // handle hit radius in canvas px
    // Corners first so a click near a corner resizes BOTH axes (diagonal). For a
    // narrow/short box the edge midpoint can fall inside the same hit radius as
    // the corner, so we pick the NEAREST handle — corners win when aimed at.
    let best = null, bestD = Infinity, isCorner = false;
    for (const [k, [hx, hy]] of Object.entries(pts)) {
      const dx = Math.abs(pt.x - hx), dy = Math.abs(pt.y - hy);
      if (dx > hs || dy > hs) continue;
      const corner = k.length === 2;
      const d = dx * dx + dy * dy;
      // Prefer a corner over an edge unless an edge is clearly nearer; among the
      // same class, nearest wins.
      if ((corner && !isCorner) || (corner === isCorner && d < bestD)) { best = k; bestD = d; isCorner = corner; }
    }
    return best;
  };

  // When a resize starts, anchor the element's CONTENT to the edge that ISN'T
  // moving, so the text/graphic stays visually put instead of riding along with
  // the dragged handle (e.g. dragging the TOP handle must not pull the text up).
  // Leaf elements only — a container keeps its own auto-layout.
  const anchorContentForResize = (el, k) => {
    if (!el || (el.querySelector && el.querySelector('[data-el]'))) return;
    const cs = el.ownerDocument.defaultView.getComputedStyle(el);
    if (!cs.display.includes('flex')) { el.style.display = 'flex'; el.style.flexDirection = 'column'; }
    const row = (el.style.flexDirection || cs.flexDirection) === 'row';
    if (k.includes('n')) { if (row) el.style.alignItems = 'flex-end'; else el.style.justifyContent = 'flex-end'; }
    else if (k.includes('s')) { if (row) el.style.alignItems = 'flex-start'; else el.style.justifyContent = 'flex-start'; }
    if (k.includes('w')) { el.style.textAlign = 'right'; if (row) el.style.justifyContent = 'flex-end'; else el.style.alignItems = 'flex-end'; }
    else if (k.includes('e')) { el.style.textAlign = 'left'; if (row) el.style.justifyContent = 'flex-start'; else el.style.alignItems = 'flex-start'; }
  };

  const onPointerDown = (e) => {
    if (editingText) return;
    if (e.button !== 0) return;
    const pt = overlayPoint(e);
    const s = slideEl(); if (!s) return;

    // What real element is under the pointer (used both to guard the resize
    // handles and, below, to select / move). Pass the live selection so a click
    // WITHIN the already-selected object drills one level in (Figma model).
    const hitEl = resolveSelectable(s.ownerDocument.elementFromPoint(pt.x, pt.y), s, selRef.current);

    // 1) Resize handle of the current selection? Handles can hover over a
    // neighbouring element. A press ON the visible dot (≤10 screen px from its
    // centre) ALWAYS resizes — the dot is right under the cursor, so resizing
    // is unambiguously what the user means; the old guard rejected this and
    // silently selected/dragged the element underneath instead. Only a press
    // in the looser hit halo that lands squarely on a DIFFERENT element keeps
    // selecting that neighbour.
    const handle = handleAt(pt);
    const onDot = (() => {
      if (!handle) return false;
      const a = handlePts()[handle];
      return Math.hypot(pt.x - a[0], pt.y - a[1]) <= 10 / scale;
    })();
    if (handle && selRef.current && (onDot || !hitEl || hitEl === selRef.current || selRef.current.contains(hitEl))) {
      const el = liftToAbsolute(selRef.current, s);
      anchorContentForResize(selRef.current, handle);
      dragRef.current = {
        kind: 'resize', handle, el: selRef.current,
        start: pt, startRect: canvasRect(selRef.current, s), targets: collectSnapTargets(selRef.current),
      };
      e.target.setPointerCapture?.(e.pointerId);
      return;
    }

    // 2) Otherwise hit-test the slide DOM to select / start a move.
    const el = hitEl;
    if (!el) { clearSelection(); lastTapRef.current = null; return; }
    // Note a possible double-tap, but DON'T commit to text editing yet: we
    // decide on pointer-up. If the pointer drags, it's a move; only a second
    // tap that releases in place (no drag) opens text editing — so "click to
    // select, then press-drag to move" is never mistaken for a double-click.
    // Double-tap is keyed on the POINT (not the resolved element): a click can
    // drill to a deeper element than the previous tap, so element identity is no
    // longer stable across two taps — a same-spot second tap is the double-tap.
    const lt = lastTapRef.current;
    const dbl = lt && (Date.now() - lt.t) < 400 && Math.abs(pt.x - lt.x) < 8 && Math.abs(pt.y - lt.y) < 8;
    lastTapRef.current = { el, t: Date.now(), x: pt.x, y: pt.y };
    selectElement(el);
    dragRef.current = {
      kind: 'move', el, start: pt, startRect: canvasRect(el, s), moved: false, lifted: false,
      targets: collectSnapTargets(el), maybeText: dbl ? pt : null,
    };
    e.target.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const g = dragRef.current;
    if (!g) return;
    const pt = overlayPoint(e);
    const dx = pt.x - g.start.x;
    const dy = pt.y - g.start.y;
    const el = g.el;
    const vLines = []; const hLines = [];

    if (g.kind === 'move') {
      if (!g.moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) {
        g.moved = true;
        // First real movement — lift to absolute now (preserving geometry),
        // then re-read snap targets against the post-reflow sibling layout.
        if (!g.lifted) { g.startRect = liftToAbsolute(el, slideEl()); g.lifted = true; g.targets = collectSnapTargets(el); }
      }
      if (!g.moved) return;
      let nx = g.startRect.x + dx;
      let ny = g.startRect.y + dy;
      const w = g.startRect.w, h = g.startRect.h;
      // Snap left/centre/right and top/middle/bottom independently.
      const sx = bestSnap([nx, nx + w / 2, nx + w], g.targets.v);
      const sy = bestSnap([ny, ny + h / 2, ny + h], g.targets.h);
      if (sx) { nx += sx.delta; vLines.push(sx.line); }
      if (sy) { ny += sy.delta; hLines.push(sy.line); }
      // Keep at least a sliver on-canvas so an element can never be lost.
      nx = Math.max(40 - w, Math.min(CANVAS_W - 40, nx));
      ny = Math.max(40 - h, Math.min(CANVAS_H - 40, ny));
      setCanvasXY(el, nx, ny);
    } else if (g.kind === 'resize') {
      let { x, y, w, h } = g.startRect;
      const k = g.handle;
      if (k.includes('e')) w = Math.max(8, g.startRect.w + dx);
      if (k.includes('s')) h = Math.max(8, g.startRect.h + dy);
      if (k.includes('w')) { const nw = Math.max(8, g.startRect.w - dx); x = g.startRect.x + (g.startRect.w - nw); w = nw; }
      if (k.includes('n')) { const nh = Math.max(8, g.startRect.h - dy); y = g.startRect.y + (g.startRect.h - nh); h = nh; }
      // Snap the moving edges.
      if (k.includes('e')) { const sn = bestSnap([x + w], g.targets.v); if (sn) { w += sn.delta; vLines.push(sn.line); } }
      if (k.includes('w')) { const sn = bestSnap([x], g.targets.v); if (sn) { x += sn.delta; w -= sn.delta; vLines.push(sn.line); } }
      if (k.includes('s')) { const sn = bestSnap([y + h], g.targets.h); if (sn) { h += sn.delta; hLines.push(sn.line); } }
      if (k.includes('n')) { const sn = bestSnap([y], g.targets.h); if (sn) { y += sn.delta; h -= sn.delta; hLines.push(sn.line); } }
      el.style.width = `${Math.round(w)}px`;
      el.style.height = `${Math.round(h)}px`;
      setCanvasXY(el, x, y);
      if (el.querySelector(':scope > [data-fit-inner]')) applyFitScale(el);
    }
    setGuides({ v: vLines, h: hLines });
    refreshSelRect();
    // Measurement badges: distance from the selected box to the slide margins.
    const r = canvasRect(el, slideEl());
    setBadges(measurementBadges(r));
  };

  const endGesture = () => {
    const g = dragRef.current;
    dragRef.current = null;
    setGuides({ v: [], h: [] });
    setBadges([]);
    if (!g) return;
    if (g.kind === 'move' && !g.moved) {
      // Released in place. A second tap (no drag) opens text editing — on the
      // INNERMOST text under the point (a click selects the whole box, but a
      // double-tap means "edit this specific line"), falling back to the box.
      if (g.maybeText) {
        lastTapRef.current = null;
        const s = slideEl();
        const inner = s && resolveTarget(s.ownerDocument.elementFromPoint(g.maybeText.x, g.maybeText.y), s);
        startTextEdit(inner || g.el, g.maybeText);
      }
      return; // otherwise a pure click — selection only
    }
    pushHistory();
  };

  // Distance-to-margin badges (Figma-style spacing hints).
  function measurementBadges(r) {
    const out = [];
    out.push({ x: r.x / 2, y: r.y + r.h / 2, val: Math.round(r.x), axis: 'h' });                       // left gap
    out.push({ x: r.x + r.w + (CANVAS_W - (r.x + r.w)) / 2, y: r.y + r.h / 2, val: Math.round(CANVAS_W - (r.x + r.w)), axis: 'h' }); // right gap
    out.push({ x: r.x + r.w / 2, y: r.y / 2, val: Math.round(r.y), axis: 'v' });                       // top gap
    out.push({ x: r.x + r.w / 2, y: r.y + r.h + (CANVAS_H - (r.y + r.h)) / 2, val: Math.round(CANVAS_H - (r.y + r.h)), axis: 'v' }); // bottom gap
    return out;
  }

  // Enter inline text editing on `el`, placing the caret at the click point
  // `pt` (canvas coords = the iframe's own coordinate system) so typing inserts
  // where you clicked rather than replacing the whole block. Shared by the
  // double-tap path in onPointerDown and the native dblclick fallback.
  const startTextEdit = (el, pt) => {
    const s = slideEl(); if (!s || !el) return;
    dragRef.current = null;          // cancel any pending move gesture
    selectElement(el);
    el.setAttribute('contenteditable', 'true');
    setEditingText(true);
    // Defer focus so the overlay's pointer-events:none has taken effect.
    setTimeout(() => {
      el.focus();
      try {
        const d = s.ownerDocument;
        let range = null;
        if (pt && d.caretRangeFromPoint) range = d.caretRangeFromPoint(pt.x, pt.y);
        else if (pt && d.caretPositionFromPoint) {
          const cp = d.caretPositionFromPoint(pt.x, pt.y);
          if (cp) { range = d.createRange(); range.setStart(cp.offsetNode, cp.offset); }
        }
        if (range && el.contains(range.startContainer)) range.collapse(true);
        else { range = d.createRange(); range.selectNodeContents(el); }
        const selObj = iframeRef.current.contentWindow.getSelection();
        selObj.removeAllRanges(); selObj.addRange(range);
      } catch { /* caret placement best-effort */ }
    }, 0);
  };
  const onDoubleClick = (e) => {
    const s = slideEl(); if (!s) return;
    const pt = overlayPoint(e);
    // Editing text targets the innermost text under the cursor, not the box.
    startTextEdit(resolveTarget(s.ownerDocument.elementFromPoint(pt.x, pt.y), s), pt);
  };

  const stopTextEdit = React.useCallback((commit) => {
    const el = selRef.current;
    if (el) el.removeAttribute('contenteditable');
    setEditingText(false);
    if (commit) pushHistory();
    refreshSelRect();
  }, [pushHistory, refreshSelRect]);

  // ---- rich-text formatting (while editing text) ---------------------------
  // B/I/U + colour apply to the CURRENT SELECTION (per character) via the iframe
  // document's execCommand; line-height + letter-spacing apply to the text block.
  const FMT_COLORS = ['#FF6428', '#202020', '#FFFFFF', '#93867D', '#6C5F5B', '#715AAE', '#00AF37', '#DE3015'];
  const fmt = (cmd, val) => { const d = idoc(); if (!d) return; try { d.execCommand('styleWithCSS', false, true); } catch { /* ignore */ } try { d.execCommand(cmd, false, val); } catch { /* ignore */ } pushHistory(); };
  const reseedSpacing = () => {
    const el = selRef.current; if (!el) return;
    try { const cs = el.ownerDocument.defaultView.getComputedStyle(el); const fsPx = parseFloat(cs.fontSize) || 16;
      const lhPx = cs.lineHeight === 'normal' ? fsPx * 1.2 : (parseFloat(cs.lineHeight) || fsPx * 1.2);
      const lsPx = cs.letterSpacing === 'normal' ? 0 : (parseFloat(cs.letterSpacing) || 0);
      setSelStyle(ss => ss ? { ...ss, lineHeightPct: Math.round((lhPx / fsPx) * 100), letterEm: +(lsPx / fsPx).toFixed(3) } : ss);
    } catch { /* ignore */ }
  };
  const bumpBlock = (prop, step, unit, base, min, max) => {
    const el = selRef.current; if (!el) return;
    const cur = parseFloat(el.style[prop]) || base;
    const next = Math.max(min, Math.min(max, +(cur + step).toFixed(3)));
    el.style[prop] = unit ? next + unit : String(next);
    pushHistory(); refreshSelRect(); reseedSpacing();
  };
  // Per-SELECTION letter-spacing: read the current spacing (em) at the selection,
  // step it, and wrap the selected characters in a span carrying the new value.
  const bumpSpacingSel = (delta) => {
    const d = idoc(); const w = iframeRef.current && iframeRef.current.contentWindow;
    const sel = w && w.getSelection(); if (!d || !sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    let node = sel.anchorNode; node = node && (node.nodeType === 3 ? node.parentElement : node);
    const cs = node ? d.defaultView.getComputedStyle(node) : null;
    const fs = cs ? (parseFloat(cs.fontSize) || 16) : 16;
    const lsPx = cs && cs.letterSpacing !== 'normal' ? (parseFloat(cs.letterSpacing) || 0) : 0;
    const next = Math.max(-0.1, Math.min(0.5, +((lsPx / fs) + delta).toFixed(3)));
    const range = sel.getRangeAt(0);
    const span = d.createElement('span'); span.setAttribute('style', `letter-spacing:${next}em`);
    try { range.surroundContents(span); }
    catch { const frag = range.extractContents(); span.appendChild(frag); range.insertNode(span); }
    sel.removeAllRanges(); const nr = d.createRange(); nr.selectNodeContents(span); sel.addRange(nr);
    pushHistory();
  };
  // Per-SELECTION style: wrap the selected characters/words in a span carrying
  // arbitrary CSS (font-family, font-size, …) — this is how font & size apply to
  // just the selection, not the whole element. Falls back to extract+insert when
  // the range straddles existing spans.
  const styleSel = (cssText) => {
    const d = idoc(); const w = iframeRef.current && iframeRef.current.contentWindow;
    const sel = w && w.getSelection(); if (!d || !sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const span = d.createElement('span'); span.setAttribute('style', cssText);
    try { range.surroundContents(span); }
    catch { const frag = range.extractContents(); span.appendChild(frag); range.insertNode(span); }
    sel.removeAllRanges(); const nr = d.createRange(); nr.selectNodeContents(span); sel.addRange(nr);
    pushHistory();
  };
  // 9-point alignment: sets where content sits WITHIN the element's frame.
  // Does NOT move the element — only changes flex alignment + text-align.
  // When in auto-expand mode, re-anchors the frame after the content reflows.
  // 9-point grid → align the CONTENT/children WITHIN the element (Figma-style):
  // for a text element it places the text; for a group it places the children
  // along/across the auto-layout axis. Direction-aware (row vs column).
  const alignInEl = (h, v) => {
    const el = selRef.current, s = slideEl(); if (!el || !s) return;
    if (!el.dataset.edLifted) liftToAbsolute(el, s);
    const oldR = canvasRect(el, s);
    const cs = el.ownerDocument.defaultView.getComputedStyle(el);
    if (!cs.display.includes('flex')) { el.style.display = 'flex'; el.style.flexDirection = 'column'; }
    const row = (el.style.flexDirection || cs.flexDirection) === 'row';
    const H = { left: 'flex-start', center: 'center', right: 'flex-end' }[h];
    const V = { top: 'flex-start', middle: 'center', bottom: 'flex-end' }[v];
    el.style.justifyContent = row ? H : V;   // main axis = flow direction
    el.style.alignItems = row ? V : H;       // cross axis
    el.style.textAlign = h;
    // For a content-hugging vertical box, re-anchor so middle/bottom is visible.
    const isAuto = !row && !el.hasAttribute('data-ed-clip') && !el.querySelector(':scope > [data-fit-inner]');
    if (isAuto) {
      void el.offsetHeight;
      const newR = canvasRect(el, s);
      if (v === 'bottom') setCanvasXY(el, oldR.x, oldR.y + oldR.h - newR.h);
      else if (v === 'middle') { const ctr = oldR.y + oldR.h / 2; setCanvasXY(el, oldR.x, ctr - newR.h / 2); }
    }
    refreshSelRect(); pushHistory('align-in');
  };
  // 9-point alignment of the SELECTED element to the slide safe area (margins read
  // live from the standard via getMargins → tokens.md).
  const align9 = (h, v) => {
    const el = selRef.current, s = slideEl(); if (!el || !s) return;
    const r = liftToAbsolute(el, s);   // {x,y,w,h} in slide space
    const mg = getMargins();
    const x = h === 'left' ? mg.left : h === 'right' ? CANVAS_W - mg.right - r.w : Math.round((CANVAS_W - r.w) / 2);
    const y = v === 'top' ? mg.top : v === 'bottom' ? CANVAS_H - mg.bottom - r.h : Math.round((CANVAS_H - r.h) / 2);
    setCanvasXY(el, x, y);
    pushHistory(); refreshSelRect();
  };
  // The on-canvas 9-point grid: POSITION the element within the slide safe area
  // (so placement is always visible — even when a text box hugs its content and
  // has no inner room) AND set the content alignment within the box (text-align +
  // flex) so a wide box also aligns its text.
  const align9grid = (h, v) => {
    const el = selRef.current, s = slideEl(); if (!el || !s) return;
    const r = liftToAbsolute(el, s);
    const mg = getMargins();
    el.style.display = 'flex'; el.style.flexDirection = 'column';
    el.style.justifyContent = { top: 'flex-start', middle: 'center', bottom: 'flex-end' }[v];
    el.style.alignItems = { left: 'flex-start', center: 'center', right: 'flex-end' }[h];
    el.style.textAlign = h;
    const x = h === 'left' ? mg.left : h === 'right' ? CANVAS_W - mg.right - r.w : Math.round((CANVAS_W - r.w) / 2);
    const y = v === 'top' ? mg.top : v === 'bottom' ? CANVAS_H - mg.bottom - r.h : Math.round((CANVAS_H - r.h) / 2);
    setCanvasXY(el, x, y);
    refreshSelRect(); pushHistory('align9grid');
  };
  // Union height of an element's content (its child boxes), in canvas px — works
  // whether children are in normal flow OR absolute-pinned (edit mode pins a
  // container's children, so plain height:auto would collapse to nothing).
  const contentHeightOf = (el) => {
    const er = el.getBoundingClientRect();
    let bottom = er.top;
    el.querySelectorAll(':scope > *').forEach(ch => { const r = ch.getBoundingClientRect(); if (r.height) bottom = Math.max(bottom, r.bottom); });
    return Math.max(0, bottom - er.top);
  };
  // How far down an element may GROW before it meets the next element below it
  // (horizontally overlapping), so Grow fills its OWN slot instead of running
  // over the content beneath it. Returns a canvas-space Y; floors at the margin.
  const growFloorBelow = (el, s) => {
    const mg = getMargins();
    const er = canvasRect(el, s);
    let floor = CANVAS_H - mg.bottom;
    s.querySelectorAll('[data-el]').forEach(o => {
      if (o === el || el.contains(o) || o.contains(el)) return;
      const r = canvasRect(o, s);
      const overlapX = r.x < er.x + er.w - 2 && r.x + r.w > er.x + 2;
      if (overlapX && r.y >= er.y + er.h - 2) floor = Math.min(floor, r.y);
    });
    return floor;
  };
  const setSizeMode = (mode) => {
    const el = selRef.current, s = slideEl(); if (!el || !s) return;
    if (!el.dataset.edLifted) liftToAbsolute(el, s);
    const oldR = canvasRect(el, s);
    const cs = el.ownerDocument.defaultView.getComputedStyle(el);
    const jc = cs.justifyContent || '';
    // Capture the layout height before clearing mode (so auto→clip/fit keeps it).
    const layoutH = el.offsetHeight;
    // Clear previous mode markers.
    el.removeAttribute('data-ed-clip');
    el.removeAttribute('data-ed-fit');
    el.removeAttribute('data-ed-grow');
    el.style.flex = '';   // reset any 'grow' fill-height
    const pw = el.parentElement;
    if (pw && pw.dataset && pw.dataset.edGrow) { pw.style.flex = ''; pw.style.minHeight = ''; delete pw.dataset.edGrow; }
    const fitWrap = el.querySelector(':scope > [data-fit-inner]');
    if (fitWrap) { while (fitWrap.firstChild) el.insertBefore(fitWrap.firstChild, fitWrap); fitWrap.remove(); }
    if (mode === 'auto') {
      el.style.overflow = '';
      el.style.height = 'auto';
      void el.offsetHeight;
      // A container's children may be absolute-pinned (edit mode), so height:auto
      // collapses — fall back to the measured extent of its content so Hug fits.
      const flowH = el.offsetHeight;
      const unionH = Math.round(contentHeightOf(el));
      if (el.children.length && unionH > flowH + 1) { el.style.height = `${unionH}px`; void el.offsetHeight; }
      const newR = canvasRect(el, s);
      if (jc.includes('end')) setCanvasXY(el, oldR.x, oldR.y + oldR.h - newR.h);
      else if (jc.includes('center')) { const ctr = oldR.y + oldR.h / 2; setCanvasXY(el, oldR.x, ctr - newR.h / 2); }
    } else if (mode === 'clip') {
      el.setAttribute('data-ed-clip', '1');
      el.style.overflow = 'hidden';
      el.style.height = `${Math.round(layoutH)}px`;
    } else if (mode === 'fit') {
      el.setAttribute('data-ed-clip', '1');
      el.style.overflow = 'hidden';
      el.style.height = `${Math.round(layoutH)}px`;
      applyFitScale(el);
    } else if (mode === 'fill') {
      // Scale the text to the object's FULL height (up or down) — keep whatever
      // box height the element currently has so the text fills it.
      el.setAttribute('data-ed-clip', '1');
      el.setAttribute('data-ed-fit', 'fill');
      el.style.overflow = 'hidden';
      applyFitScale(el);
    } else if (mode === 'grow') {
      // Fill the available slot: extend the box height DOWN until it meets the
      // next element below (or the safe-area bottom if nothing's there), so Grow
      // fills its own space without running over the content beneath it.
      const r = canvasRect(el, s);
      el.style.height = `${Math.max(40, Math.round(growFloorBelow(el, s) - r.y))}px`;
      el.style.overflow = '';
      el.setAttribute('data-ed-grow', '1');
    }
    refreshSelRect(); pushHistory('size-mode');
  };
  const applyFitScale = (el) => {
    let inner = el.querySelector(':scope > [data-fit-inner]');
    if (!inner) {
      const d = el.ownerDocument;
      inner = d.createElement('div');
      inner.setAttribute('data-fit-inner', '');
      inner.style.cssText = 'width:100%;transform-origin:top left;';
      while (el.firstChild) inner.appendChild(el.firstChild);
      el.appendChild(inner);
    }
    inner.style.transform = 'none';
    void inner.offsetHeight;
    const ecs = el.ownerDocument.defaultView.getComputedStyle(el);
    const padT = parseFloat(ecs.paddingTop) || 0;
    const padB = parseFloat(ecs.paddingBottom) || 0;
    const sh = inner.scrollHeight || 1;
    const avail = (el.clientHeight - padT - padB) || sh;
    // 'fill' scales UP or down to the object's full height; 'fit' only scales down.
    const fill = el.getAttribute('data-ed-fit') === 'fill';
    const scale = fill ? Math.max(0.2, Math.min(8, avail / sh)) : Math.min(1, avail / sh);
    inner.style.transform = Math.abs(scale - 1) > 0.002 ? `scale(${+scale.toFixed(4)})` : 'none';
  };
  const setPadding = (px) => {
    const el = selRef.current; if (!el) return;
    const n = Math.max(0, Math.round(Number(px) || 0));
    el.style.padding = `${n}px`;
    const mode = selStyle?.sizeMode;
    if (mode === 'fit') applyFitScale(el);
    if (mode === 'auto') {
      const s = slideEl(); if (s) {
        const oldR = canvasRect(el, s);
        const cs = el.ownerDocument.defaultView.getComputedStyle(el);
        const jc = cs.justifyContent || '';
        void el.offsetHeight;
        const newR = canvasRect(el, s);
        if (jc.includes('end')) setCanvasXY(el, oldR.x, oldR.y + oldR.h - newR.h);
        else if (jc.includes('center')) { const ctr = oldR.y + oldR.h / 2; setCanvasXY(el, oldR.x, ctr - newR.h / 2); }
      }
    }
    refreshSelRect(); pushHistory('padding');
  };

  // ---- keyboard ------------------------------------------------------------
  React.useEffect(() => {
    const onKey = (e) => {
      // Undo / redo work everywhere.
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      if (meta && (e.key === 'y' || e.key === 'Y')) { e.preventDefault(); redo(); return; }
      if (editingText) {
        if (e.key === 'Escape') { e.preventDefault(); stopTextEdit(true); }
        return;
      }
      // Copy / paste / duplicate.
      if (meta && (e.key === 'c' || e.key === 'C')) { if (selRef.current) { e.preventDefault(); copySelected(); } return; }
      if (meta && (e.key === 'v' || e.key === 'V')) { if (clipboardRef.current) { e.preventDefault(); pasteClipboard(); } return; }
      if (meta && (e.key === 'd' || e.key === 'D')) { if (selRef.current) { e.preventDefault(); duplicateSelected(); } return; }
      if (e.key === 'Escape') { setMenu(null); clearSelection(); return; }
      const focused = document.activeElement;
      const inField = focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA' || focused.tagName === 'SELECT' || focused.isContentEditable);
      const el = selRef.current;
      if (!el) return;
      if (inField) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        el.remove();
        clearSelection();
        pushHistory();
        return;
      }
      if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        const s = slideEl(); if (!s) return;
        if (!el.dataset.edLifted) liftToAbsolute(el, s);
        const step = e.shiftKey ? 10 : 1;
        const cs = el.style;
        const left = parseFloat(cs.left || '0'); const top = parseFloat(cs.top || '0');
        if (e.key === 'ArrowLeft') cs.left = `${left - step}px`;
        if (e.key === 'ArrowRight') cs.left = `${left + step}px`;
        if (e.key === 'ArrowUp') cs.top = `${top - step}px`;
        if (e.key === 'ArrowDown') cs.top = `${top + step}px`;
        refreshSelRect();
        pushHistory('nudge');
      }
    };
    // Share the latest handler with the iframe document (attached on each load
    // in onFrameLoad) so Ctrl/⌘+C/V/Z/D, Delete and arrows fire whether focus
    // is in the parent overlay OR inside the slide iframe.
    keyHandlerRef.current = onKey;
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingText]);

  // ---- iframe load: seed history + bind text-edit blur ---------------------
  const onFrameLoad = () => {
    const d = idoc();
    if (!d) return;
    const doFlatten = () => {
      if (idxRef.current !== -1) return;
      const s = d.querySelector('.slide');
      if (s) { try { flattenSlide(s); } catch { /* best-effort */ } }
      historyRef.current = [{ html: `<!doctype html>\n${d.documentElement.outerHTML}`, key: null }];
      idxRef.current = 0;
      setHist({ idx: 0, len: 1 });
    };
    if (idxRef.current === -1) {
      (d.fonts?.ready || Promise.resolve()).then(doFlatten).catch(doFlatten);
    }
    reloadingRef.current = false;
    // Read the underlying L2 zone rects (the [data-fit] containers) in canvas
    // coords so the overlay can show them in cyan.
    try {
      const s = d.querySelector('.slide'); const sr = s && s.getBoundingClientRect();
      if (sr) setZoneRects([...d.querySelectorAll('.content-v2 [data-fit]')].map(z => { const r = z.getBoundingClientRect(); return { x: r.left - sr.left, y: r.top - sr.top, w: r.width, h: r.height }; }));
    } catch { /* ignore */ }
    // Mirror the shortcut handler onto the iframe document (fresh per load) so
    // Ctrl/⌘+C/V/Z/D etc. work even when focus sits inside the slide.
    d.addEventListener('keydown', (e) => { if (keyHandlerRef.current) keyHandlerRef.current(e); });
    // Commit text edits when focus leaves the EDITABLE element itself. We must
    // gate on e.target === the editable: focusing it moves focus off <body>,
    // whose focusout bubbles here too — without this gate that stray event
    // cancels editing the instant it starts. Also ignore moves that stay inside
    // the editable subtree.
    d.addEventListener('focusout', (e) => {
      const ed = selRef.current;
      if (!ed || ed.getAttribute('contenteditable') !== 'true') return;
      if (e.target !== ed) return;
      if (e.relatedTarget && ed.contains(e.relatedTarget)) return;
      stopTextEdit(true);
    });
    clearSelection();
  };

  // ---- undo / redo / recover ----------------------------------------------
  const restore = (i) => {
    const stack = historyRef.current;
    if (i < 0 || i >= stack.length) return;
    idxRef.current = i;
    reloadingRef.current = true;
    clearSelection();
    setHist({ idx: i, len: stack.length });
    setDoc(stack[i].html);          // reloads the iframe with this snapshot
    setDirty(i !== 0);
  };
  const undo = () => restore(idxRef.current - 1);
  const redo = () => restore(idxRef.current + 1);

  // Replace the working document wholesale (recover original / revert) and make
  // it the new top of history so it's undoable.
  const loadFreshDoc = (rawHtml) => {
    const edited = prepareEditDoc(rawHtml);
    const stack = historyRef.current;
    stack.length = idxRef.current + 1;
    stack.push({ html: edited, key: null });
    if (stack.length > 100) stack.shift();
    idxRef.current = stack.length - 1;
    reloadingRef.current = true;
    clearSelection();
    setHist({ idx: idxRef.current, len: stack.length });
    setDoc(edited);
    setDirty(true);
  };

  const recover = async (which) => {
    if (!loadVersionHtml) return;
    setRecovering(true);
    try {
      const raw = await loadVersionHtml(which);
      if (raw) loadFreshDoc(raw);
    } finally {
      setRecovering(false);
    }
  };

  const doSave = () => {
    if (editingText) stopTextEdit(true);
    const out = serialize(true);
    if (out) onSave(out);
  };

  const canUndo = hist.idx > 0;
  const canRedo = hist.idx < hist.len - 1;

  // Geometry of the rendered overlay = canvas coords × scale, rounded to whole
  // screen pixels so the selection box / handles / guides rasterise exactly on
  // the same boundaries as the (quantised-scale) slide content underneath.
  const px = (v) => Math.round(v * scale);
  const handleKeys = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  const handlePos = (k) => {
    if (!sel) return { left: 0, top: 0 };
    const x = sel.x + (k.includes('e') ? sel.w : k.includes('w') ? 0 : sel.w / 2);
    const y = sel.y + (k.includes('s') ? sel.h : k.includes('n') ? 0 : sel.h / 2);
    return { left: px(x), top: px(y) };
  };

  const tbBtn = { width: 26, height: 24, cursor: 'pointer', borderRadius: 5, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
  return (
    <div className={`slide-editor${fill ? ' slide-editor--fill' : ''}`}>
      <div className="slide-editor__stage" style={{ position: 'relative' }}>
        {/* Floating preview toolbar — undo / redo + zoom / fit, on the canvas. */}
        <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 20, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(20,16,14,0.82)', borderRadius: 8, padding: '4px 6px', boxShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
          <button type="button" title="Undo (⌘Z)" disabled={!canUndo} onClick={undo} style={{ ...tbBtn, opacity: canUndo ? 1 : 0.4 }}>⟲</button>
          <button type="button" title="Redo (⌘⇧Z)" disabled={!canRedo} onClick={redo} style={{ ...tbBtn, opacity: canRedo ? 1 : 0.4 }}>⟳</button>
          <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.18)', margin: '0 2px' }} />
          <button type="button" title="Zoom out" onClick={() => setZoom(z => Math.max(0.4, +(z - 0.2).toFixed(2)))} style={tbBtn}>−</button>
          <span style={{ color: '#fff', fontSize: 11, minWidth: 38, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
          <button type="button" title="Zoom in" onClick={() => setZoom(z => Math.min(3, +(z + 0.2).toFixed(2)))} style={tbBtn}>+</button>
          <button type="button" title="Fit to screen" disabled={zoom === 1} onClick={() => setZoom(1)} style={{ ...tbBtn, width: 'auto', padding: '0 8px', fontSize: 11, opacity: zoom === 1 ? 0.4 : 1 }}>Fit</button>
        </div>
        {/* The canvas fills the stage; default (zoom 1) fits the whole slide, no scroll. */}
        <div ref={zoomScrollRef} style={{ overflow: 'auto', flex: '1 1 auto', minHeight: 360 }}>
        <div
          ref={wrapRef}
          className="slide-editor__canvas"
          style={{ width: scale > 0 ? CANVAS_W * scale : '100%', height: scale > 0 ? CANVAS_H * scale : undefined, aspectRatio: scale > 0 ? undefined : '16 / 9', margin: 'auto', flex: 'none', position: 'relative', background: '#FCF8F7', borderRadius: 'var(--r-md)', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
        >
          <iframe
            ref={iframeRef}
            title={`Slide ${slideIndex} editor`}
            srcDoc={doc}
            onLoad={() => { onFrameLoad(); setLoadedDoc(doc); }}
            sandbox="allow-same-origin allow-scripts"
            style={{
              width: CANVAS_W, height: CANVAS_H, border: 0,
              transform: `scale(${scale})`, transformOrigin: 'top left',
              position: 'absolute', top: 0, left: 0,
              opacity: (loadedDoc === doc && scale > 0) ? 1 : 0,
              transition: 'opacity 0.12s ease',
            }}
          />
          {/* Master badge — which Level-1 master (background) this slide sits on. */}
          {master && scale > 0 && (
            <div style={{ position: 'absolute', left: 8, top: 8, zIndex: 6, pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: 'rgba(20,16,14,0.72)', color: '#fff', font: '600 11px/1.2 system-ui, sans-serif', letterSpacing: '0.02em' }}>
              <span style={{ opacity: 0.6, fontWeight: 500 }}>master</span> {master}
            </div>
          )}
          {/* THE 40px modular grid — the ONE grid everything snaps to. Drawn in
              magenta: faint minor lines every 40px (transparent) + bolder major
              lines every 200px (full), so the foundation reads without noise. */}
          {show40 && scale > 0 && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 3 }}>
              {Array.from({ length: Math.floor(CANVAS_W / 40) }, (_, i) => (i + 1) * 40).map(x => <div key={`g40v${x}`} style={{ position: 'absolute', left: x * scale, top: 0, width: 1, height: '100%', background: x % 200 === 0 ? 'rgba(255,0,128,0.55)' : 'rgba(255,0,128,0.16)' }} />)}
              {Array.from({ length: Math.floor(CANVAS_H / 40) }, (_, i) => (i + 1) * 40).map(y => <div key={`g40h${y}`} style={{ position: 'absolute', top: y * scale, left: 0, height: 1, width: '100%', background: y % 200 === 0 ? 'rgba(255,0,128,0.55)' : 'rgba(255,0,128,0.16)' }} />)}
            </div>
          )}
          {/* Cyan Level-2 containers (the zone boxes the template defines). */}
          {showZones && scale > 0 && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 4 }}>
              {zoneRects.map((z, i) => (
                <div key={i} style={{ position: 'absolute', left: z.x * scale, top: z.y * scale, width: z.w * scale, height: z.h * scale, border: '1px solid rgba(0,184,217,0.9)', background: 'rgba(0,184,217,0.06)' }} />
              ))}
            </div>
          )}
          {/* Magenta safe-area margins, read live from the standard (getMargins → tokens.md). */}
          {showGuides && scale > 0 && (() => {
            const mg = getMargins();
            return (
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 4 }}>
                {[mg.left, CANVAS_W - mg.right].map(x => <div key={`vx${x}`} style={{ position: 'absolute', left: x * scale, top: 0, width: 1, height: '100%', background: 'rgba(255,0,128,0.6)' }} />)}
                {[mg.top, CANVAS_H - mg.bottom].map(y => <div key={`hy${y}`} style={{ position: 'absolute', top: y * scale, left: 0, height: 1, width: '100%', background: 'rgba(255,0,128,0.6)' }} />)}
              </div>
            );
          })()}
          {/* Editing overlay — captures pointer events except while typing text. */}
          <div
            className="slide-editor__overlay"
            onPointerDown={(e) => { setMenu(null); onPointerDown(e); }}
            onPointerMove={onPointerMove}
            onPointerUp={endGesture}
            onPointerCancel={endGesture}
            onDoubleClick={onDoubleClick}
            onContextMenu={(e) => {
              e.preventDefault();
              const s = slideEl(); if (!s) return;
              const pt = overlayPoint(e);
              const hit = resolveSelectable(s.ownerDocument.elementFromPoint(pt.x, pt.y), s, selRef.current);
              if (hit) selectElement(hit);
              const r = wrapRef.current.getBoundingClientRect();
              setMenu({ x: e.clientX - r.left, y: e.clientY - r.top, onEl: !!hit });
            }}
            style={{ position: 'absolute', inset: 0, pointerEvents: editingText ? 'none' : 'auto', cursor: 'default' }}
          >
            {showGrid && scale > 0 && (
              <svg className="slide-editor__grid" width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                {/* Brand column grid (6 columns) — clearly visible orange hairlines. */}
                {GRID_V.map((x, i) => <line key={`v${i}`} x1={px(x)} y1={0} x2={px(x)} y2="100%" stroke="rgba(255,100,40,0.45)" strokeWidth="1" />)}
                {/* Named zone rows (title/content/footer) — dashed so they read apart from the columns. */}
                {GRID_H.map((y, i) => <line key={`h${i}`} x1={0} y1={px(y)} x2="100%" y2={px(y)} stroke="rgba(255,100,40,0.32)" strokeWidth="1" strokeDasharray="6 5" />)}
              </svg>
            )}
            {/* Alignment guides while dragging. */}
            {guides.v.map((x, i) => <div key={`gv${i}`} className="slide-editor__guide" style={{ left: px(x), top: 0, width: 1, height: '100%' }} />)}
            {guides.h.map((y, i) => <div key={`gh${i}`} className="slide-editor__guide" style={{ top: px(y), left: 0, height: 1, width: '100%' }} />)}
            {/* Measurement badges. */}
            {badges.map((b, i) => (
              <div key={`b${i}`} className="slide-editor__badge" style={{ left: px(b.x), top: px(b.y) }}>{b.val}</div>
            ))}
            {/* Selection box + handles. The box border PULSES and carries a
                blinking anchor dot at its top-left, so "where am I right now —
                what will my next add/change hit" is always visible at a glance. */}
            <style>{`@keyframes edSelPulse{0%,100%{box-shadow:0 0 0 1px rgba(255,100,40,0.95)}50%{box-shadow:0 0 0 1px rgba(255,100,40,0.25)}}@keyframes edDotBlink{0%,49%{opacity:1}50%,100%{opacity:0.15}}`}</style>
            {sel && (
              <div className="slide-editor__selbox" style={{ left: px(sel.x), top: px(sel.y), width: px(sel.w), height: px(sel.h), animation: editingText ? 'none' : 'edSelPulse 1.1s ease-in-out infinite' }}>
                <div className="slide-editor__seltag">{sel.label}</div>
                {!editingText && <div style={{ position: 'absolute', left: -5, top: -5, width: 10, height: 10, borderRadius: '50%', background: '#FF6428', border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.15)', animation: 'edDotBlink 1.1s steps(1) infinite' }} />}
              </div>
            )}
            {sel && !editingText && handleKeys.map((k) => {
              const p = handlePos(k);
              return <div key={k} className={`slide-editor__handle slide-editor__handle--${k}`} style={{ left: p.left, top: p.top }} />;
            })}
            {/* Right-click context menu. */}
            {menu && (
              <div className="slide-editor__menu" style={{ left: menu.x, top: menu.y }} onPointerDown={e => e.stopPropagation()}>
                {menu.onEl && <button type="button" onClick={() => runMenuAction('copy')}>Copy <span>⌘C</span></button>}
                <button type="button" disabled={!clipboardRef.current} onClick={() => runMenuAction('paste')}>Paste <span>⌘V</span></button>
                {menu.onEl && <button type="button" onClick={() => runMenuAction('duplicate')}>Duplicate <span>⌘D</span></button>}
                {menu.onEl && <button type="button" onClick={() => runMenuAction('parent')}>Select parent</button>}
                {menu.onEl && <div className="slide-editor__menu-sep" />}
                {menu.onEl && <button type="button" onClick={() => runMenuAction('front')}>Bring to front</button>}
                {menu.onEl && <button type="button" onClick={() => runMenuAction('back')}>Send to back</button>}
                {menu.onEl && <div className="slide-editor__menu-sep" />}
                {menu.onEl && <button type="button" className="danger" onClick={() => runMenuAction('delete')}>Delete <span>Del</span></button>}
              </div>
            )}
          </div>
        </div>
        </div>
        <div className="slide-editor__hint">
          {editingText
            ? 'Editing text — Esc or click away to finish.'
            : sel
              ? 'Drag to move · drag a handle to resize · double-click to edit text · arrows nudge · Delete removes.'
              : 'Click an element to select it. Double-click to edit its text.'}
        </div>
        {/* Docked edit bar — sticks below the preview. Coords + 9-point align +
            text colour/size/style + fill + padding + field/layout for the
            selected element. Updates live on move / resize. */}
        <div style={{ position: 'sticky', bottom: 0, zIndex: 12, marginTop: 'var(--space-2)' }}>
          {(() => {
            const inpS = { width: 74, MozAppearance: 'textfield', appearance: 'textfield', fontSize: 12, padding: '3px 6px', border: '1px solid #3a3a3a', background: '#141414', color: '#fff', borderRadius: 3 };
            const sw = { width: 22, height: 18, padding: 0, border: '1px solid #3a3a3a', background: '#141414', borderRadius: 3, cursor: 'pointer' };
            const miniBtn = { padding: '0 7px', height: 18, cursor: 'pointer', borderRadius: 3, border: '1px solid #3a3a3a', background: '#141414', color: '#fff', fontSize: 12, lineHeight: '16px' };
            if (editingText) {
              // Per-character / per-word formatting — acts on the SELECTED text
              // run (highlight a word, then style it). onMouseDown preventDefault
              // keeps the text selection alive when a control is clicked.
              return (
                <div onMouseDown={e => e.preventDefault()}
                  style={{ position: 'relative', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', columnGap: 12, rowGap: 6, alignItems: 'center', background: 'rgba(20,16,14,0.92)', borderRadius: 8, padding: '8px 12px', boxShadow: '0 -2px 8px rgba(0,0,0,0.3)' }}>
                  <span style={{ color: '#FF6428', fontSize: 11, fontWeight: 700 }}>Text — selected run</span>
                  <button type="button" title="Bold" onClick={() => fmt('bold')} style={{ ...miniBtn, fontWeight: 800, padding: '0 9px' }}>B</button>
                  <button type="button" title="Italic" onClick={() => fmt('italic')} style={{ ...miniBtn, fontStyle: 'italic', padding: '0 9px' }}>I</button>
                  <button type="button" title="Underline" onClick={() => fmt('underline')} style={{ ...miniBtn, textDecoration: 'underline', padding: '0 9px' }}>U</button>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#9a9a9a', fontSize: 10 }} title="Font">Aa
                    <select onChange={e => { if (e.target.value) { styleSel(`font-family:${e.target.value}`); e.target.selectedIndex = 0; } }} defaultValue="" style={{ fontSize: 10, background: '#141414', color: '#fff', border: '1px solid #3a3a3a', borderRadius: 3, padding: '1px 2px', cursor: 'pointer' }}>
                      <option value="">Font…</option>
                      <option value="var(--font-sans)">Sans</option>
                      <option value="var(--font-display)">Display</option>
                    </select>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#9a9a9a', fontSize: 10 }} title="Font size of the selected run (px)">S
                    <NumInput value="" min={1} onCommit={n => styleSel(`font-size:${n}px`)} style={{ ...inpS, width: 50 }} />
                  </label>
                  <span style={{ color: '#9a9a9a', fontSize: 10 }}>Colour</span>
                  {BRAND_COLORS.map(c => (
                    <button key={c.value} type="button" title={c.name} onClick={() => fmt('foreColor', c.value)} style={{ width: 16, height: 16, padding: 0, cursor: 'pointer', borderRadius: 3, background: c.value, border: c.value === '#FFFFFF' ? '1px solid #888' : '1px solid rgba(255,255,255,0.18)' }} />
                  ))}
                  <span style={{ color: '#9a9a9a', fontSize: 10, marginLeft: 6 }}>Esc to finish</span>
                </div>
              );
            }
            if (!sel) {
              return (
                <div style={{ display: 'flex', alignItems: 'center', minHeight: 34, background: 'rgba(20,16,14,0.92)', borderRadius: 8, padding: '6px 12px', color: '#9a9a9a', fontSize: 12, boxShadow: '0 -2px 8px rgba(0,0,0,0.25)' }}>
                  Select an element to edit its position, size, colour, padding and layout here. Double-click to edit text and format individual words.
                </div>
              );
            }
            return (
              <div onPointerDown={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}
                style={{ position: 'relative', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', columnGap: 14, rowGap: 6, alignItems: 'center', background: 'rgba(20,16,14,0.92)', borderRadius: 8, padding: '8px 12px', boxShadow: '0 -2px 8px rgba(0,0,0,0.3)' }}>
                <span style={{ color: '#FF6428', fontSize: 11, fontWeight: 700, letterSpacing: '0.02em' }}>{sel.label}</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,11px)', gridTemplateRows: 'repeat(3,11px)', gap: 2 }} title="Align content (9-point)">
                  {ALIGN9.map(([h, v]) => { const on = selStyle && selStyle.alignH === h && selStyle.alignV === v; return (
                    <button key={h + v} type="button" onClick={() => alignInEl(h, v)} title={`${v}-${h}`} style={{ width: 11, height: 11, padding: 0, cursor: 'pointer', borderRadius: 2, border: on ? '1px solid #FF6428' : '1px solid #444', background: on ? 'rgba(255,100,40,0.3)' : '#1c1c1c' }} />
                  ); })}
                </div>
                {['x', 'y', 'w', 'h'].map(f => (
                  <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#9a9a9a', fontSize: 10, fontWeight: 600 }}>{f.toUpperCase()}
                    <NumInput value={Math.round(sel[f])} min={(f === 'w' || f === 'h') ? 1 : undefined} onCommit={n => applyGeom(f, n)} style={inpS} />
                  </label>
                ))}
                <label style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#9a9a9a', fontSize: 10 }} title="Text colour (brand palette)">A
                  <button type="button" onClick={() => setColorPop(p => p === 'text' ? null : 'text')} style={{ ...sw, background: toHex(selStyle?.color) }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#9a9a9a', fontSize: 10 }} title="Font size (px)">S
                  <NumInput value={selStyle?.fontSize || ''} min={1} onCommit={n => applyStyle('fontSize', `${n}px`, { key: 'fontSize' })} style={{ ...inpS, width: 50 }} />
                </label>
                <select title="Text style" value={selStyle?.fontFamilyKind || 'var(--font-sans)'} onChange={e => applyStyle('fontFamily', e.target.value, { cascade: true })} style={{ fontSize: 10, background: '#141414', color: '#fff', border: '1px solid #3a3a3a', borderRadius: 3, padding: '1px 2px', cursor: 'pointer' }}>
                  <option value="var(--font-sans)">Sans</option>
                  <option value="var(--font-display)">Display</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#9a9a9a', fontSize: 10 }} title="Background fill (brand palette)">BG
                  <button type="button" onClick={() => setColorPop(p => p === 'fill' ? null : 'fill')} style={{ ...sw, background: 'repeating-conic-gradient(#777 0 25%,#444 0 50%) 50%/8px 8px' }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#9a9a9a', fontSize: 10 }} title="Line height (%)">↕
                  <NumInput value={selStyle?.lineHeightPct ?? ''} min={1} onCommit={n => setLine(n)} style={{ ...inpS, width: 56 }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#9a9a9a', fontSize: 10 }} title="Letter spacing (em)">A↔
                  <NumInput value={selStyle?.letterEm ?? ''} step="0.01" onCommit={n => setChar(n)} style={{ ...inpS, width: 60 }} />
                </label>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }} title="Inner padding per side (T R B L)">
                  <span style={{ color: '#9a9a9a', fontSize: 10 }}>Pad</span>
                  {['Top', 'Right', 'Bottom', 'Left'].map(side => (
                    <NumInput key={side} title={`Padding ${side}`} value={selStyle ? (selStyle['pad' + side[0]] ?? 0) : 0} onCommit={n => setPad(side.toLowerCase(), n)} style={{ ...inpS, width: 38 }} />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span style={{ color: '#9a9a9a', fontSize: 10 }} title="What the field does when the text doesn't fit">Field</span>
                  {[['auto', 'Hug', 'Field height hugs the content (expands as you type)'], ['grow', 'Grow', 'Box fills its zone slot — gives the 9-grid room to place the content'], ['clip', 'Mask', 'Cut / mask content that overflows the field'], ['fill', 'Fill', 'Scale the text to the full height of the object']].map(([m, lbl, tip]) => (
                    <button key={m} type="button" title={tip} onClick={() => setSizeMode(m)} style={{ ...miniBtn, padding: '0 8px', border: selStyle?.sizeMode === m ? '1px solid #FF6428' : '1px solid #3a3a3a', background: selStyle?.sizeMode === m ? 'rgba(255,100,40,0.28)' : '#141414' }}>{lbl}</button>
                  ))}
                </div>
                {selStyle?.isContainer && (
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ color: '#9a9a9a', fontSize: 10 }} title="How the objects inside are arranged (auto-layout)">Lay</span>
                    {[['free', 'Free', 'Objects positioned freely'], ['row', 'Row', 'In line'], ['wrap', 'Wrap', 'In line, auto-break to the next row'], ['col', 'Col', 'One per line']].map(([m, lbl, tip]) => (
                      <button key={m} type="button" title={tip} onClick={() => setLayout(m)} style={{ ...miniBtn, padding: '0 7px', border: selStyle?.layoutMode === m ? '1px solid #FF6428' : '1px solid #3a3a3a', background: selStyle?.layoutMode === m ? 'rgba(255,100,40,0.28)' : '#141414' }}>{lbl}</button>
                    ))}
                    {selStyle?.layoutMode !== 'free' && <><span style={{ color: '#9a9a9a', fontSize: 10, marginLeft: 4 }} title="Gap between objects">Gap</span><NumInput title="Gap between objects" value={selStyle?.gap ?? 0} onCommit={n => setGap(n)} style={{ ...inpS, width: 44 }} /></>}
                  </div>
                )}
                {colorPop && (
                  <div style={{ position: 'absolute', bottom: '100%', left: 12, marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 3, width: 150, background: 'rgba(10,8,7,0.96)', borderRadius: 5, padding: 4, boxShadow: '0 2px 10px rgba(0,0,0,0.4)' }}>
                    {colorPop === 'fill' && <button type="button" title="None" onClick={() => { applyStyle('backgroundColor', 'transparent', { key: 'colour' }); setColorPop(null); }} style={{ width: 16, height: 16, padding: 0, cursor: 'pointer', borderRadius: 3, border: '1px solid #888', background: 'repeating-conic-gradient(#777 0 25%,#333 0 50%) 50%/7px 7px' }} />}
                    {BRAND_COLORS.map(c => (
                      <button key={c.value} type="button" title={c.name} onClick={() => { applyStyle(colorPop === 'text' ? 'color' : 'backgroundColor', c.value, colorPop === 'text' ? { cascade: true, key: 'colour' } : { key: 'colour' }); setColorPop(null); }}
                        style={{ width: 16, height: 16, padding: 0, cursor: 'pointer', borderRadius: 3, background: c.value, border: c.value === '#FFFFFF' ? '1px solid #888' : '1px solid rgba(255,255,255,0.18)' }} />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        {belowCanvas}
      </div>

      {/* Right-hand edit menu — every control lives here (nothing on top of the canvas). */}
      <aside className="slide-editor__rail">
        <div className="slide-editor__rail-top">
          <div className="slide-editor__rail-head">{slideTitle || `Edit slide ${slideIndex + 1}`}</div>
          <div className="slide-editor__rail-actions">
            <button type="button" className="btn-ghost" disabled={busy} onClick={onCancel}>{cancelLabel || 'Cancel'}</button>
            <button type="button" className="btn-orange" disabled={busy || (!dirty && !saveLabel)} onClick={doSave}>
              {busy ? 'Saving…' : (saveLabel || 'Save')}
            </button>
          </div>
        </div>

        {/* Slide-level controls passed in by the host (template/background, …). */}
        {railTop}

        {/* Layers — the full nested hierarchy, live from the rendered slide. Click a
            row to select it on the canvas (the inspector below then aligns / styles
            it); ▸ expands a box to reach its inner text. */}
        {(() => {
          const nodes = buildLayerNodes(slideEl());
          const renderLayer = (node, depth, key, siblings, idx) => {
            const hasKids = node.children && node.children.length > 0;
            const open = !collapsedRef.current.has(node.el);
            const selected = !node.isZone && node.el === selRef.current;
            const prev = siblings && idx > 0 ? siblings[idx - 1] : null;
            const next = siblings && idx < siblings.length - 1 ? siblings[idx + 1] : null;
            return (
              <React.Fragment key={key}>
                <div className={`slide-editor__layer${selected ? ' is-sel' : ''}${node.isZone ? ' is-zone' : ''}`} style={{ paddingLeft: 4 + depth * 13 }} onClick={() => { if (!node.isZone) selectElement(node.el); }}>
                  {hasKids
                    ? <button type="button" className="slide-editor__caret" onClick={(e) => { e.stopPropagation(); toggleCollapse(node.el); }}>{open ? '▾' : '▸'}</button>
                    : <span className="slide-editor__caret slide-editor__caret--leaf" />}
                  <span className="slide-editor__layer-label">{node.label}</span>
                  {node.text && <span className="slide-editor__layer-text">{node.text}</span>}
                  {!node.isZone && (
                    <span className="slide-editor__layer-ctl">
                      <button type="button" title="Move up (reorders on the slide too)" disabled={!prev} onClick={(e) => { e.stopPropagation(); if (prev) moveLayer(node.el, prev.el, -1); }}>↑</button>
                      <button type="button" title="Move down (reorders on the slide too)" disabled={!next} onClick={(e) => { e.stopPropagation(); if (next) moveLayer(node.el, next.el, 1); }}>↓</button>
                      <button type="button" title="Add a text sub-element inside this one" onClick={(e) => { e.stopPropagation(); addChildElement(node.el); }}>+</button>
                      <button type="button" title="Delete this element" onClick={(e) => { e.stopPropagation(); selectElement(node.el); deleteSelected(); }}>×</button>
                    </span>
                  )}
                  {node.isZone && onAddTo && (
                    <span className="slide-editor__layer-ctl">
                      <button type="button" title={`Add an element to ${node.label}`} onClick={(e) => { e.stopPropagation(); onAddTo(String(node.label).toLowerCase()); }}>＋</button>
                    </span>
                  )}
                </div>
                {hasKids && open && node.children.map((c, i) => renderLayer(c, depth + 1, `${key}.${i}`, node.children, i))}
              </React.Fragment>
            );
          };
          return (
            <div className="slide-editor__group" data-tick={treeTick}>
              <div className="slide-editor__group-title">Layers</div>
              {nodes.length
                ? <div className="slide-editor__layers">{nodes.map((n, i) => renderLayer(n, 0, String(i), nodes, i))}</div>
                : <div className="slide-editor__empty">Open a slide to see its element hierarchy.</div>}
            </div>
          );
        })()}

        {/* View — guides, the 40px grid, and zoom (scroll over the canvas also zooms). */}
        <div className="slide-editor__group">
          <div className="slide-editor__group-title">View</div>
          <div className="slide-editor__row">
            <button type="button" className={`btn-ghost${showGuides ? ' is-on' : ''}`} onClick={() => setShowGuides(g => !g)} title="Magenta — the brand safe-area margins (where furniture sits), read live from the standard">{showGuides ? '✓ Margins' : 'Margins'} <span style={{ color: '#FF0080' }}>◾</span></button>
            <button type="button" className={`btn-ghost${showZones ? ' is-on' : ''}`} onClick={() => setShowZones(g => !g)} title="Cyan — the Level-2 container boxes the template defines">{showZones ? '✓ Containers' : 'Containers'} <span style={{ color: '#00B8D9' }}>◾</span></button>
          </div>
          <div className="slide-editor__row">
            <button type="button" className={`btn-ghost${show40 ? ' is-on' : ''}`} onClick={() => setShow40(g => !g)} title="The 40px modular grid (magenta) — the ONE foundation grid everything snaps to. Faint minor lines every 40px, bold major every 200px.">{show40 ? '✓ 40px grid' : '40px grid'} <span style={{ color: '#FF0080' }}>◾</span></button>
            <button type="button" className={`btn-ghost${showGrid ? ' is-on' : ''}`} onClick={() => setShowGrid(g => !g)} title="Legacy 6-column brand grid (orange) — OFF by default; the 40px grid is the foundation now">{showGrid ? '✓ Column grid (legacy)' : 'Column grid (legacy)'}</button>
          </div>
          <div className="slide-editor__note">Zoom / fit + undo / redo live on the canvas toolbar. Scroll over the canvas to zoom. Right-click an element for copy / paste / duplicate / order; ⌘C/⌘V/⌘D and arrows also work.</div>
        </div>

        {/* Text format — only while editing text inline (B/I/U, colour, line & char spacing). */}
        {editingText && (
          <div className="slide-editor__group" onMouseDown={(e) => e.preventDefault()}>
            <div className="slide-editor__group-title">Text format</div>
            <div className="slide-editor__row">
              <button type="button" title="Bold" onClick={() => fmt('bold')} style={{ fontWeight: 800, width: 30, height: 28, cursor: 'pointer', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg)' }}>B</button>
              <button type="button" title="Italic" onClick={() => fmt('italic')} style={{ fontStyle: 'italic', width: 30, height: 28, cursor: 'pointer', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg)' }}>I</button>
              <button type="button" title="Underline" onClick={() => fmt('underline')} style={{ textDecoration: 'underline', width: 30, height: 28, cursor: 'pointer', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg)' }}>U</button>
              {FMT_COLORS.map(c => <button key={c} type="button" title={c} onClick={() => fmt('foreColor', c)} style={{ width: 18, height: 18, borderRadius: '50%', cursor: 'pointer', background: c, border: '1px solid rgba(0,0,0,0.2)' }} />)}
            </div>
            <div className="slide-editor__row" style={{ alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--fg-muted)', minWidth: 44 }} title="Zeilenhöhe">Line</span>
              <button type="button" className="btn-ghost" style={{ padding: '2px 9px' }} onClick={() => bumpBlock('lineHeight', -0.05, '', 1.15, 0.7, 3)}>−</button>
              <button type="button" className="btn-ghost" style={{ padding: '2px 9px' }} onClick={() => bumpBlock('lineHeight', 0.05, '', 1.15, 0.7, 3)}>+</button>
              <span style={{ fontSize: 11, color: 'var(--fg-muted)', minWidth: 56 }} title="Abstand — selected text">Spacing</span>
              <button type="button" className="btn-ghost" style={{ padding: '2px 9px' }} onClick={() => bumpSpacingSel(-0.01)}>−</button>
              <button type="button" className="btn-ghost" style={{ padding: '2px 9px' }} onClick={() => bumpSpacingSel(0.01)}>+</button>
            </div>
          </div>
        )}

        <div className="slide-editor__group">
          <div className="slide-editor__group-title">Selection</div>
          {sel ? (
            <>
              <div className="slide-editor__seltagline">{sel.label}</div>
              {/* Position & size live only in the on-canvas edit table. */}
              <div className="slide-editor__row">
                <button type="button" className="btn-ghost" onClick={selectParent} title="Select the containing group">↑ Parent</button>
                <button type="button" className="btn-ghost" onClick={duplicateSelected} title="Duplicate (⌘D)">⧉ Copy</button>
                <button type="button" className="btn-ghost" onClick={deleteSelected} title="Delete (Del)">🗑</button>
              </div>
            </>
          ) : (
            <div className="slide-editor__empty">Nothing selected. Click an element on the slide.</div>
          )}
        </div>

        {/* Align & sizing, Type and Colour now live in the on-canvas edit table. */}

        {/* Change image — replace a selected image via AI generation, an Unsplash
            search, or a direct upload (POST /api/slide-system/image). */}
        {sel && selStyle?.isImage && (
          <div className="slide-editor__group">
            <div className="slide-editor__group-title">Image</div>
            <div className="slide-editor__note" style={{ marginBottom: 4 }}>Replace this image — generate one, search Unsplash, or upload your own.</div>
            <textarea className="textarea" rows={2} placeholder="Describe an image to generate…" value={imgPrompt} onChange={e => setImgPrompt(e.target.value)} disabled={imgBusy} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} />
            <button type="button" className="btn-orange" style={{ marginTop: 4, width: '100%' }} disabled={imgBusy || !imgPrompt.trim()} onClick={() => changeImage({ mode: 'generate', prompt: imgPrompt.trim() })}>{imgBusy ? 'Working…' : '✨ Generate image'}</button>
            <div className="slide-editor__row" style={{ marginTop: 6, gap: 6 }}>
              <input className="input" placeholder="Search Unsplash…" value={imgQ} onChange={e => setImgQ(e.target.value)} disabled={imgBusy} onKeyDown={e => { if (e.key === 'Enter' && imgQ.trim()) changeImage({ mode: 'unsplash', query: imgQ.trim() }); }} style={{ flex: 1, fontSize: 12 }} />
              <button type="button" className="btn-ghost" disabled={imgBusy || !imgQ.trim()} onClick={() => changeImage({ mode: 'unsplash', query: imgQ.trim() })}>Search</button>
            </div>
            <label className="btn-ghost" style={{ marginTop: 6, display: 'block', cursor: imgBusy ? 'default' : 'pointer', textAlign: 'center' }}>
              ⬆ Upload image
              <input type="file" accept="image/*" disabled={imgBusy} style={{ display: 'none' }} onChange={e => { const f = e.target.files && e.target.files[0]; if (f) { const fd = new FormData(); fd.append('file', f); changeImage(fd, true); } e.target.value = ''; }} />
            </label>
            {imgErr && <div className="slide-editor__note" style={{ color: '#A20235' }}>{imgErr}</div>}
          </div>
        )}

        {/* Change slide type — re-generates the slide into another archetype. */}
        {onChangeType && (
          <div className="slide-editor__group">
            <div className="slide-editor__group-title">Slide type</div>
            <select className="slide-editor__select" value={typeChoice} onChange={e => setTypeChoice(e.target.value)}>
              <option value="">{currentLayout ? `Current: ${currentLayout}` : 'Choose archetype…'}</option>
              {ARCHETYPES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <button
              type="button"
              className="btn-ghost"
              disabled={busy || !typeChoice || typeChoice === currentLayout}
              onClick={() => onChangeType(typeChoice)}
              title="Re-generate this slide in the chosen archetype. This is an AI redesign — it replaces the current hand-edited layout."
            >↻ Change type &amp; regenerate</button>
            <div className="slide-editor__note">Regenerating re-derives the slide from its text — it discards manual edits. Use “First generation” to get back.</div>
          </div>
        )}

        {railBottom}
      </aside>
    </div>
  );

  // ---- rail helpers (defined after render for closure over refs) ----------
  function setGeom(prop, raw) {
    const el = selRef.current; const s = slideEl();
    if (!el || !s) return;
    if (!el.dataset.edLifted) liftToAbsolute(el, s);
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    if (prop === 'width' || prop === 'height') {
      el.style[prop] = `${Math.round(n)}px`;
      if (el.querySelector(':scope > [data-fit-inner]')) applyFitScale(el);
    } else {
      const r = canvasRect(el, s);
      let parent = el.parentElement;
      while (parent && parent !== s && !parent.dataset.edLifted) parent = parent.parentElement;
      const isNested = parent && parent !== s && parent !== el && !parent.classList?.contains('zfit');
      const ofs = isNested ? canvasRect(parent, s) : { x: 0, y: 0 };
      const absN = n + (isNested ? (prop === 'left' ? ofs.x : ofs.y) : 0);
      setCanvasXY(el, prop === 'left' ? absN : r.x, prop === 'top' ? absN : r.y);
    }
    refreshSelRect();
    pushHistory(`geom-${prop}`);
  }
  function selectParent() {
    const el = selRef.current; const s = slideEl();
    if (!el || !s) return;
    let p = el.parentElement;
    while (p && !isSelectable(p, s)) p = p.parentElement;
    if (p) selectElement(p);
  }
  function deleteSelected() {
    const el = selRef.current;
    if (!el) return;
    el.remove();
    clearSelection();
    pushHistory();
  }

  // Reorder a layer relative to a visual neighbour. Absolute (lifted) siblings
  // swap their on-canvas positions; in-flow siblings (a tile's number/label) are
  // reordered in the DOM. Either way the tree (sorted by visual position) follows.
  function moveLayer(el, target, dir) {
    const s = slideEl(); if (!el || !target || !s) return;
    const view = s.ownerDocument.defaultView;
    const elAbs = el.dataset.edLifted === '1' || view.getComputedStyle(el).position === 'absolute';
    const tgAbs = target.dataset.edLifted === '1' || view.getComputedStyle(target).position === 'absolute';
    if (elAbs && tgAbs) {
      const a = canvasRect(el, s), b = canvasRect(target, s);
      liftToAbsolute(el, s); liftToAbsolute(target, s);
      setCanvasXY(el, b.x, b.y); setCanvasXY(target, a.x, a.y);
    } else if (el.parentElement === target.parentElement) {
      if (dir < 0) el.parentElement.insertBefore(el, target);
      else el.parentElement.insertBefore(el, target.nextSibling);
    } else {
      return;   // different containers, not absolutely positioned — nothing safe to do
    }
    selectElement(el);
    pushHistory();
  }

  // Add a fresh, editable text sub-element inside `parentEl`. It inherits colour
  // and lands at the brand body size; double-click (or it's already selected) to type.
  function addChildElement(parentEl) {
    const d = idoc(); if (!d || !parentEl) return;
    const n = d.createElement('div');
    n.textContent = 'New text';
    n.style.cssText = 'font-family:var(--font-sans);font-size:40px;line-height:1.2;color:inherit;margin:0;';
    parentEl.appendChild(n);
    if (collapsedRef.current.has(parentEl)) { collapsedRef.current.delete(parentEl); setTreeTick(t => t + 1); }
    selectElement(n);
    pushHistory();
  }

  // Apply an inline style to the selection (colour / type). `opts.cascade`
  // also writes it onto every descendant — needed for colour and font family /
  // weight, because slide text usually carries its OWN explicit colour/font on
  // the inner element, which would otherwise override an inherited value set on
  // a selected container. Font SIZE never cascades (it would flatten a block's
  // type hierarchy) — it applies to the selected element only.
  function applyStyle(prop, value, opts) {
    const el = selRef.current;
    if (!el) return;
    el.style[prop] = value;
    if (opts && opts.cascade) el.querySelectorAll('*').forEach(c => { c.style[prop] = value; });
    refreshSelRect();
    pushHistory((opts && opts.key) || `style-${prop}`);
  }
  function clearFill() {
    const el = selRef.current;
    if (!el) return;
    el.style.backgroundColor = 'transparent';
    refreshSelRect();
    pushHistory('style-fill');
  }

  // Copy / paste / duplicate. The clipboard holds a detached clone's markup.
  function copySelected() {
    const el = selRef.current;
    if (!el) return;
    const c = el.cloneNode(true);
    c.removeAttribute('contenteditable');
    clipboardRef.current = c.outerHTML;
  }
  function pasteClipboard() {
    const el = selRef.current; const s = slideEl();
    if (!clipboardRef.current || !s) return;
    const host = (el && el.parentElement && isSelectable(el.parentElement, s)) ? el.parentElement : s;
    const tpl = idoc().createElement('div');
    tpl.innerHTML = clipboardRef.current;
    const node = tpl.firstElementChild;
    if (!node) return;
    host.appendChild(node);
    // Lift + offset so the paste is visibly distinct from its source.
    const r = canvasRect(node, s);
    liftToAbsolute(node, s);
    setCanvasXY(node, Math.min(CANVAS_W - 60, r.x + 24), Math.min(CANVAS_H - 60, r.y + 24));
    selectElement(node);
    pushHistory();
  }
  function duplicateSelected() { copySelected(); pasteClipboard(); }
  function bringToFront() { const el = selRef.current; if (!el) return; el.style.zIndex = '60'; pushHistory('z'); }
  function sendToBack() { const el = selRef.current; if (!el) return; el.style.zIndex = '1'; pushHistory('z'); }

  // Right-click context menu actions, dispatched by label.
  function runMenuAction(act) {
    setMenu(null);
    if (act === 'copy') copySelected();
    else if (act === 'paste') pasteClipboard();
    else if (act === 'duplicate') duplicateSelected();
    else if (act === 'delete') deleteSelected();
    else if (act === 'front') bringToFront();
    else if (act === 'back') sendToBack();
    else if (act === 'parent') selectParent();
  }
}
